import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { Fragment, useEffect, type FormEvent, type MouseEvent } from 'react'
import { useState } from 'react'
import { ApiError } from '../../api/client'
import {
  addCustomKit,
  addCustomPackage,
  addKit,
  agentCommandsQuery,
  agentCommandsTableQuery,
  alarmMatrixQuery,
  applicationsQuery,
  applicationsTableQuery,
  approveApplication,
  changeApplicationCategory,
  changeApplicationTag,
  cmdCategoriesTableQuery,
  cmdCommandsQuery,
  cmdCommandsTableQuery,
  cmdSubcategories,
  cmdTypesTableQuery,
  createCmdCategory,
  createCmdCommand,
  createCmdCommandType,
  createStandardCommand,
  customKitsQuery,
  customKitsTableQuery,
  deleteCustomKit,
  deleteCustomPackage,
  disapproveApplication,
  editAgentCommand,
  editCmdCategory,
  editCmdCommandType,
  hardwareAlarmsQuery,
  importCmdBuiltins,
  insertAgentCommand,
  insertMacosCommand,
  isOpteemumTab,
  kitsQuery,
  kitsTableQuery,
  macosCommandsTableQuery,
  newCategory,
  opteemumShellQuery,
  packagesQuery,
  packagesTableQuery,
  removeCmdCategory,
  removeCmdCommand,
  removeCmdCommandType,
  removeKit,
  removeStandardCommand,
  saveEditAgentCommand,
  standardCommandsQuery,
  standardCommandsTableQuery,
  startupTableQuery,
  taggingTableQuery,
  updateCmdCategory,
  updateCmdCommand,
  updateCmdCommandType,
  updateStandardCommand,
  type AlarmBand,
  type AlarmSignal,
  type OpteemumTab,
} from '../../api/settingsOpteemum'
import { isDiagnosticReportPage } from '../../api/settingsDiagnosticReports'
import { DiagnosticReportsSettingsPage } from '../diagnostic_reports/DiagnosticReportsSettingsPage'
import { EndpointPanel } from '../inventoree/InventoreeSettingsPage'
import { Modal } from '../../ui/Modal'
import { newAlert, toast } from '../../ui/notify'
import { SelectPicker } from '../../ui/SelectPicker'
/* Capul ecranelor din zona de setari, cu foaia lui. Aceeasi piesa ca pe
   Customers, Inventoree si Softsee. */
import { SettingsCheck, SettingsHead, SettingsPicker, SettingsPickerItem } from '../../ui/SettingsParts'
import { Spinner } from '../../ui/Spinner'
import { SoftseeListChrome } from '../softsee/SoftseeListChrome'

/**
 * Settings → Opteemum: index.php + fiecare ecran din opteemum_settings/,
 * element cu element. Diagnostic reports e /app/settings/diagnostic_reports/*.
 */

const GROUPS = [
  { id: 'CMD', icon: 'fas fa-terminal', label: 'CMD' },
  { id: 'Standard Commands', icon: 'fas fa-road', label: 'Standard Commands' },
  { id: 'Agent Commands', icon: 'fas fa-window-restore', label: 'Agent Commands' },
  { id: 'MACOS Commands', icon: 'fas fa-window-restore', label: 'MACOS Commands' },
  { id: 'Software', icon: 'fas fa-list', label: 'Software' },
] as const

/**
 * „Endpoint Performance Status" - randul venit din Settings › Inventoree.
 *
 * Ecranul e panoul Inventoree (`EndpointPanel`), dar randul e al meniului
 * asta, si se deschide AICI: meniul ramane pe ecran, randul se aprinde, panoul
 * se deseneaza in continut - ca la filele de Diagnostic reports, care sunt tot
 * ale altei zone. Un singur panou, doua locuri din care se cheama.
 *
 * Adresa lui ramane cea a Inventoree, `/app/settings/inventoree/
 * endpoint_status_settings`: dupa ea sunt semnele de carte, `routes.php`,
 * `application/Services/V4/SettingsCut.php` si redirectul portalului vechi.
 * Ea deschide tot pagina asta - `inventoreeEndpointSettingsRoute` din
 * `router.tsx`.
 */
const ENDPOINT_TAB = 'endpoint_status_settings'

/** Filele pe care pagina stie sa le deseneze: ale ei, ale Diagnostic reports si Endpoint. */
function isKnownTab(tab: string): boolean {
  return isOpteemumTab(tab) || isDiagnosticReportPage(tab) || tab === ENDPOINT_TAB
}

export function OpteemumSettingsPage({ tab }: { tab: string }) {
  const navigate = useNavigate()
  const shell = useQuery(opteemumShellQuery())
  /*
   * Ce a deschis omul cu mana, peste ce spune ecranul pe care sta.
   *
   * Lipseste dintr-un cap = hotaraste tabul curent: capul sub care sta randul
   * aprins e deschis, restul stranse. Se sterge la fiecare mutare, ca sa nu
   * ramana strans chiar capul in care tocmai a intrat - altfel randul aprins
   * ar fi ascuns. Aceeasi socoteala ca la meniul Easeedesk.
   *
   * Sta inaintea iesirilor de mai jos: un hook nu se sare.
   */
  const [opened, setOpened] = useState<Record<string, boolean>>({})
  useEffect(() => setOpened({}), [tab])

  useEffect(() => {
    if (!shell.data) return
    if (tab !== '' && isKnownTab(tab)) return
    const first = shell.data.tabs[0]
    if (!first) return
    if (isDiagnosticReportPage(first.id)) {
      void navigate({ to: '/settings/diagnostic_reports/$page', params: { page: first.id } })
      return
    }
    void navigate({ to: '/settings/opteemum/$tab', params: { tab: first.id } })
  }, [shell.data, tab, navigate])

  if (shell.error instanceof ApiError && shell.error.status === 403) {
    /*
     * Ecranele de Diagnostic reports n-au cerut niciodata bara Opteemum:
     * settings/Diagnostic_reports::computer_scan verifica doar dreptul scanului,
     * iar in Skus se intra din meniul Easeedesk, nu de aici. Fara bara aratam
     * doar ecranul; un refuz pe tot hubul ar inchide o usa care era deschisa.
     */
    if (isDiagnosticReportPage(tab)) return <DiagnosticReportsSettingsPage page={tab} />
    /*
     * La fel „Endpoint Performance Status": dreptul lui e al ECRANULUI
     * (`settings.inventoree_endpoint_settings.read`), nu al hub-ului - asa era
     * si cand statea in meniul Inventoree. Panoul insusi refuza daca omul nu-l
     * are; un refuz pe hub ar inchide o usa deschisa.
     */
    if (tab === ENDPOINT_TAB) return <EndpointPanel />
    return (
      <div className="alert alert-danger">
        <i className="fas fa-exclamation-circle" aria-hidden="true"></i> You do not have access to this action.
      </div>
    )
  }
  if (!shell.data) return <Spinner />

  const current = isKnownTab(tab) ? tab : (shell.data.tabs[0]?.id ?? '')
  const loose = shell.data.tabs.filter((t) => !t.group)
  const reports = shell.data.tabs.filter((t) => t.group === 'DIAGNOSTIC REPORTS')

  return (
    <>
      <aside className="od-dark-sidebar od-module-rail">
        <ul className="od-dark-sidebar-nav default-sidebar-nav">
          {/*
            Fara `style` scris in markup. Era `backgroundColor: '#1b1d25'` -
            o banda aproape neagra ramasa de pe vremea cand tot rail-ul era
            intunecat. `settings_console.css`, sectiunea 10, o scoate din
            machetele portalului chiar cu argumentul asta; copia din v4 o
            pastrase, si pana acum nu se vedea doar fiindca meniul statea strans
            la 64 de pixeli. Deschis la 270 ar fi fost singurul dreptunghi negru
            de pe ecran. Restul (indesarea, marimea, majusculele) le da tot
            sectiunea 10, pentru orice `.menu-header`.
          */}
          <li className="menu-header">Opteemum Settings</li>
          {loose.map((item) => (
            <SidebarTab key={item.id} tab={item} current={current} />
          ))}
          {GROUPS.map((group) => {
            const items = shell.data.tabs.filter((t) => t.group === group.id)
            if (items.length === 0) return null
            const open = opened[group.id] ?? items.some((t) => t.id === current)
            return (
              /* `selected` s-a scos: capul unui submeniu nu e un rand pe care
                 STAI, e o eticheta care deschide o lista, si sectiunea 24 din
                 `settings_console.css` da pastila randului ales dupa exact
                 clasa asta. Ramane `active`, care deschide lista si intoarce
                 sageata - ca la Softsee si la meniul Easeedesk.

                 `active` pe <li> SI `display` pe <ul>, amandoua din stare:
                 `app.css` ascunde orice `ul` din rail
                 (`.od-dark-sidebar-nav ul { display: none }`) si il arata
                 inapoi numai sub `li.active`. Pana acum `active` se dadea dupa
                 numarul capului in lista - `i === 0` - deci CMD statea deschis
                 oriunde ai fi fost, iar celelalte patru nu se deschideau
                 niciodata: cele noua ecrane de sub ele erau scrise in pagina si
                 nu se vedeau. Peste asta, `javascript:void(0);` fara niciun
                 `onClick`: in portalul vechi lista o desfacea
                 `navSidebarMultiLevel` din `public/js/general.js`, pe care v4
                 nu-l incarca deloc. */
              <li key={group.id} className={open ? 'submenu-header active' : 'submenu-header'}>
                <a
                  href="#"
                  aria-expanded={open}
                  onClick={(event) => {
                    event.preventDefault()
                    setOpened((was) => ({ ...was, [group.id]: !open }))
                  }}
                >
                  <i className={group.icon} aria-hidden="true"></i> {group.label}
                </a>
                <ul style={{ display: open ? 'block' : 'none' }}>
                  {items.map((item) => (
                    <SidebarTab key={item.id} tab={item} current={current} />
                  ))}
                </ul>
              </li>
            )
          })}
          <li className="menu-block-divider"></li>
          {reports.length > 0 && (
            <>
              <li className="menu-header menu-header-lead">DIAGNOSTIC REPORTS</li>
              {reports.map((item) => (
                <SidebarTab key={item.id} tab={item} current={current} />
              ))}
            </>
          )}
        </ul>
      </aside>
      <div className="page-content-wrapper">
        {current === 'hardware_alarms' && <HardwareAlarmsPanel />}
        {current === 'cmd_categories' && <CmdCategoriesPanel />}
        {current === 'cmd_command_types' && <CmdTypesPanel />}
        {current === 'cmd_commands' && <CmdCommandsPanel />}
        {current === 'standard_commands' && <StandardCommandsPanel />}
        {current === 'agent_commands' && <AgentCommandsPanel />}
        {current === 'macos_commands' && <MacosCommandsPanel />}
        {current === 'manage_applications' && <ApplicationsPanel />}
        {current === 'softwares_tagging' && <TaggingPanel />}
        {current === 'manage_kits' && <KitsPanel />}
        {current === 'manage_custom_kits' && <CustomKitsPanel />}
        {current === 'manage_custom_packages' && <PackagesPanel />}
        {current === 'startup_applications_manager' && <StartupPanel />}
        {current === ENDPOINT_TAB && <EndpointPanel />}
        {isDiagnosticReportPage(current) && <DiagnosticReportsSettingsPage page={current} />}
      </div>
    </>
  )
}

function SidebarTab({ tab, current }: { tab: OpteemumTab; current: string }) {
  const navigate = useNavigate()
  const active = tab.id === current
  const diagnostic = isDiagnosticReportPage(tab.id)
  /* Endpoint isi pastreaza ADRESA de la Inventoree, desi ecranul se deschide aici. */
  const endpoint = tab.id === ENDPOINT_TAB
  const href = tab.external
    ? '/' + tab.href.replace(/^\//, '')
    : endpoint
      ? '/app/settings/inventoree/' + tab.id
      : diagnostic
        ? '/app/settings/diagnostic_reports/' + tab.id
        : '/app/settings/opteemum/' + tab.id
  const onClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (tab.external) return
    event.preventDefault()
    if (endpoint) {
      void navigate({ to: '/settings/inventoree/endpoint_status_settings' })
      return
    }
    if (diagnostic) {
      void navigate({ to: '/settings/diagnostic_reports/$page', params: { page: tab.id } })
      return
    }
    void navigate({ to: '/settings/opteemum/$tab', params: { tab: tab.id } })
  }
  return (
    <li className={active ? 'menu-tab active' : 'menu-tab'}>
      <a href={href} data-toggle={tab.external || tab.group ? 'tab' : undefined} onClick={onClick}>
        {tab.group && !tab.external ? null : tab.icon ? <i className={tab.icon} aria-hidden="true"></i> : null} {tab.label}
      </a>
    </li>
  )
}

function num(row: Record<string, unknown>, key: string): number {
  return Number(row[key] ?? row[key.toLowerCase()] ?? 0)
}

function str(row: Record<string, unknown>, key: string): string {
  return String(row[key] ?? row[key.toLowerCase()] ?? '')
}


function Forbidden() {
  return <div className="alert alert-danger">You do not have access to this action.</div>
}



/** Gravitatea unei benzi -> pastila zonei. `od_console.css` le da fundal
    deschis si cerneala inchisa, deci se pot si citi. */
const ALARM_FLAGS: Array<{ label: string; pill: string }> = [
  { label: 'Info', pill: 'label-info' },
  { label: 'Minor', pill: 'label-success' },
  { label: 'Major', pill: 'label-warning' },
  { label: 'Critical', pill: 'label-danger' },
]

function flagOf(flag: number): { label: string; pill: string } {
  return ALARM_FLAGS[flag] ?? ALARM_FLAGS[0]
}

/** Ce scrie pe o banda: intervalul, cand semnalul are cifre, altfel gravitatea. */
function bandText(band: AlarmBand, sign: string): string {
  const range = band.dinamic ? `${band.from}–${band.to}${sign}` : flagOf(band.flag).label

  return band.factory ? `Factory ${range}` : range
}

/**
 * Propozitia de sub butoanele de strat - aceeasi ca `sentence()` din
 * `AlarmDoor`: cine mosteneste de la cine, si de unde se stinge un semnal pe o
 * singura masina.
 */
function layerSentence(layer: string, groupName: string): string {
  if (layer === 'sp') {
    return 'Global. Companies without their own bands inherit these. To silence a signal on one machine, use Now on that device.'
  }
  if (layer === 'group') {
    return `Groups · «${groupName || 'this group'}». These bands replace the company ones for machines in that group. Disable a signal on one machine from Now, on the device.`
  }

  return 'Company. Machines assigned to a group use that group instead. Disable a signal on one machine from Now, on the device.'
}

/**
 * Hardware & software alarms.
 *
 * Ecranul asta se deschidea GOL. Doua lucruri lipseau, nu unul: api-ul intorcea
 * `chapters: []` scris in cod (`DbalOpteemumSettingsSource::hardwareAlarms`), si
 * panoul de aici desena doar titlul si o propozitie - nici macar nu se uita la
 * `chapters`. Deci nici cand datele ar fi venit intregi n-ar fi aparut nimic.
 *
 * Acum vine si se vede ce vedea si portalul: stratul caruia i se aplica
 * pragurile (Global / firma / grup), si fiecare semnal al usii cu starea lui si
 * benzile lui.
 */
function HardwareAlarmsPanel() {
  const page = useQuery(hardwareAlarmsQuery())
  /* Stratul ales de om. Gol = inca n-a ales, si atunci hotaraste dreptul lui,
     ca `$defaultLayer` din macheta veche. */
  const [picked, setPicked] = useState('')
  const [group, setGroup] = useState('')
  const data = page.data
  const layer = picked || (data?.canSp ? 'sp' : data?.canCompany ? 'company' : 'group')
  const groups = data?.groups ?? []
  const groupId = Number(group || num(groups[0] ?? {}, 'ID')) || 0
  const matrix = useQuery({ ...alarmMatrixQuery(layer, groupId), enabled: data !== undefined })

  if (page.error instanceof ApiError && page.error.status === 403) return <Forbidden />
  if (!data) return <Spinner />

  const groupOptions = groups.map((g) => ({ value: String(num(g, 'ID')), label: str(g, 'group_name') }))
  const groupName = groupOptions.find((o) => o.value === String(groupId))?.label ?? ''
  const signals: AlarmSignal[] = matrix.data?.signals ?? []

  let lastChapter = ''

  return (
    <div id="content">
      <SettingsHead strong="Hardware" rest="& software alarms" subtitle={data.blurb} />

      {/* 1. Cui i se aplica pragurile. */}
      <div className="form-group" id="alarm-door" data-layer={layer} data-door={data.door}>
        <label className="control-label">Who these thresholds apply to</label>
        <div>
          <div className="btn-group" role="group">
            {data.canSp && (
              <button
                type="button"
                className={layer === 'sp' ? 'btn btn-sm btn-primary' : 'btn btn-sm btn-default'}
                data-layer="sp"
                onClick={() => setPicked('sp')}
              >
                Global
              </button>
            )}
            {data.canCompany && (
              <button
                type="button"
                className={layer === 'company' ? 'btn btn-sm btn-primary' : 'btn btn-sm btn-default'}
                data-layer="company"
                onClick={() => setPicked('company')}
              >
                Company
              </button>
            )}
            {data.canGroups && (
              <button
                type="button"
                className={layer === 'group' ? 'btn btn-sm btn-primary' : 'btn btn-sm btn-default'}
                data-layer="group"
                onClick={() => setPicked('group')}
              >
                Groups
              </button>
            )}
          </div>
          {data.canGroups && layer === 'group' && (
            /* `form-control` pe invelisul pickerului: asa iese la fel de lat ca
               o casuta de text de langa el. */
            <span style={{ display: 'inline-block', marginLeft: 8, minWidth: 220, verticalAlign: 'middle' }}>
              <SelectPicker
                id="ad-group-id"
                className="form-control"
                name="group"
                title={groupOptions.length === 0 ? 'No groups yet' : 'Select...'}
                value={String(groupId)}
                onChange={setGroup}
                options={groupOptions}
              />
            </span>
          )}
        </div>
        <p className="help-block" id="ad-sentence">
          {layerSentence(layer, groupName)}
        </p>
      </div>

      {/* 2. Fiecare semnal, cu urmarirea si benzile lui. */}
      <label className="control-label">Every signal &mdash; watch and threshold</label>
      <table
        className="table table-striped table-hover table-bordered settingsDataTable"
        width="100%"
        id="ad-matrix"
      >
        <colgroup>
          <col />
          <col style={{ width: 110 }} />
          <col style={{ width: 420 }} />
        </colgroup>
        <thead>
          <tr>
            <th className="metroTd a-left">Signal</th>
            <th className="metroTd center">Watch</th>
            <th className="metroTd a-left">Bands</th>
          </tr>
        </thead>
        <tbody>
          {matrix.isPending && (
            <tr>
              <td colSpan={3}>Loading&hellip;</td>
            </tr>
          )}
          {!matrix.isPending && signals.length === 0 && (
            <tr>
              <td colSpan={3} className="dataTables_empty">
                No signals on this door.
              </td>
            </tr>
          )}
          {signals.map((signal) => {
            const head = signal.chapter !== lastChapter ? signal.chapter : ''
            lastChapter = signal.chapter
            return (
              <Fragment key={signal.id}>
                {head !== '' && (
                  <tr className="ad-chapter-row">
                    <td colSpan={3}>
                      <strong>{head}</strong>
                    </td>
                  </tr>
                )}
                <tr data-type={signal.id}>
                  <td>
                    <strong>{signal.name}</strong>
                    {signal.hint !== '' && (
                      <>
                        {' '}
                        <span className="help-block" style={{ display: 'inline', marginLeft: 4 }} title={signal.hint}>
                          {signal.hint}
                        </span>
                      </>
                    )}
                  </td>
                  <td className="center">
                    {signal.watched ? (
                      <span className="label label-success">On</span>
                    ) : signal.loaded ? (
                      <span className="label label-default">Off</span>
                    ) : (
                      <span className="label label-default">Not loaded</span>
                    )}
                  </td>
                  <td>
                    {signal.bands.length === 0 ? (
                      <span className="label label-default">No factory bands</span>
                    ) : (
                      signal.bands.map((band, i) => (
                        <span
                          key={i}
                          className={`label ${band.on && !band.factory ? flagOf(band.flag).pill : 'label-default'}`}
                          style={{ marginRight: 4 }}
                        >
                          {bandText(band, signal.sign)}
                        </span>
                      ))
                    )}
                  </td>
                </tr>
              </Fragment>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

/**
 * Pe ce masini se aplica o categorie sau o comanda - aceleasi doua liste ca
 * `cmd/_platform_fields.php`, cu acelasi inteles: gol = peste tot. Se arata ca
 * doua liste, nu ca patru bife, fiindca „Windows si Linux" e un RASPUNS, nu
 * lipsa a doua raspunsuri.
 */
const OS_FAMILIES: Array<{ value: string; label: string }> = [
  { value: '', label: 'Windows and Linux' },
  { value: 'windows', label: 'Windows only' },
  { value: 'linux', label: 'Linux only' },
]

const DEVICE_ROLES: Array<{ value: string; label: string }> = [
  { value: '', label: 'Servers and workstations' },
  { value: 'server', label: 'Servers only' },
  { value: 'workstation', label: 'Workstations only' },
]

/**
 * O valoare pusa de mana in baza („linux,macos") nu se pierde si nu se rescrie
 * in tacere la prima salvare: se arata asa cum e, ca in macheta veche.
 */
function withStored(options: Array<{ value: string; label: string }>, stored: string) {
  if (stored === '' || options.some((o) => o.value === stored)) return options

  return [...options, { value: stored, label: `${stored} (as set in the database)` }]
}

function labelFor(options: Array<{ value: string; label: string }>, stored: string): string {
  return options.find((o) => o.value === stored)?.label ?? stored
}

/** Campurile unei categorii - aceleasi la „nou" si la „editeaza". */
type CmdCategoryFields = { name: string; os: string; role: string; icon: string; note: string; order: string }

const EMPTY_CATEGORY: CmdCategoryFields = { name: '', os: '', role: '', icon: '', note: '', order: '0' }

function CmdCategoryForm({ id, value, onChange }: { id: string; value: CmdCategoryFields; onChange(next: CmdCategoryFields): void }) {
  const set = (patch: Partial<CmdCategoryFields>) => onChange({ ...value, ...patch })

  return (
    <form method="post">
      <div className="form-group">
        <label className="control-label" htmlFor={`${id}-name`}>
          Category name:
        </label>
        <input
          className="form-control"
          id={`${id}-name`}
          name="category_name"
          type="text"
          value={value.name}
          onChange={(ev) => set({ name: ev.target.value })}
        />
      </div>
      <div className="form-group">
        <label className="control-label">Runs on:</label>
        {/* `SelectPicker` CU `form-control` pe invelis: altfel lista iese mai
            ingusta decat casuta de text de deasupra ei. */}
        <SelectPicker
          id={`${id}-os`}
          className="form-control"
          name="os_families"
          title="Windows and Linux"
          value={value.os}
          onChange={(next) => set({ os: next })}
          options={withStored(OS_FAMILIES, value.os)}
        />
        <p className="help-block">
          Where the button shows up. A Linux command does not appear on a Windows machine, and the other way round.
        </p>
      </div>
      <div className="form-group">
        <label className="control-label">Machine role:</label>
        <SelectPicker
          id={`${id}-role`}
          className="form-control"
          name="device_roles"
          title="Servers and workstations"
          value={value.role}
          onChange={(next) => set({ role: next })}
          options={withStored(DEVICE_ROLES, value.role)}
        />
        <p className="help-block">
          Laptops and workstations are one role, servers the other. Leave it on both unless the command only makes sense on one.
        </p>
      </div>
      <div className="form-group">
        <label className="control-label">Icon:</label>
        <input
          className="form-control"
          name="icon"
          type="text"
          placeholder="fas fa-cogs"
          value={value.icon}
          onChange={(ev) => set({ icon: ev.target.value })}
        />
        <p className="help-block">A Font Awesome class. Left empty, the section gets a folder.</p>
      </div>
      <div className="form-group">
        <label className="control-label">Note:</label>
        <input
          className="form-control"
          name="note"
          type="text"
          placeholder="Everything about units: what runs, what fell over."
          value={value.note}
          onChange={(ev) => set({ note: ev.target.value })}
        />
        <p className="help-block">One line under the section title, saying what it answers.</p>
      </div>
      <div className="form-group">
        <label className="control-label">Order:</label>
        <input
          className="form-control"
          name="category_order"
          type="number"
          min={0}
          value={value.order}
          onChange={(ev) => set({ order: ev.target.value })}
        />
        <p className="help-block">Smaller comes first. Equal numbers keep the order they were created in.</p>
      </div>
    </form>
  )
}

function CmdCategoriesPanel() {
  const query = useQueryClient()
  const table = useQuery(cmdCategoriesTableQuery())
  const [open, setOpen] = useState(false)
  const [editing, setEditing] = useState(0)
  const [form, setForm] = useState<CmdCategoryFields>(EMPTY_CATEGORY)
  const [importing, setImporting] = useState('')
  if (table.error instanceof ApiError && table.error.status === 403) return <Forbidden />
  if (!table.data) return <Spinner />

  const refresh = () => query.invalidateQueries({ queryKey: ['settings', 'opteemum', 'cmd-categories'] })
  const fields = (f: CmdCategoryFields) => ({
    category_name: f.name,
    os_families: f.os,
    device_roles: f.role,
    icon: f.icon,
    note: f.note,
    category_order: f.order,
  })

  const onSave = async () => {
    try {
      await createCmdCategory(fields(form))
      toast('A new category created', 'Categories', 'success')
      setOpen(false)
      setForm(EMPTY_CATEGORY)
      await refresh()
    } catch (err) {
      newAlert(err instanceof Error ? err.message : 'Error', 'danger')
    }
  }

  const onUpdate = async () => {
    try {
      await updateCmdCategory({ ...fields(form), category: editing })
      toast('Category updated', 'Categories', 'success')
      setEditing(0)
      await refresh()
    } catch (err) {
      newAlert(err instanceof Error ? err.message : 'Error', 'danger')
    }
  }

  /* Butoanele de import erau moarte: markupul lui `categories-1.js` fusese
     copiat cu `data-os` cu tot, dar fara nimic care sa le asculte. */
  const onImport = async (os: string) => {
    setImporting(os)
    try {
      const res = await importCmdBuiltins(os)
      if (res.success) {
        toast(res.msg ?? '', 'Categories', 'success')
        await refresh()
      } else {
        newAlert(res.msg ?? 'The set could not be imported.', 'danger')
      }
    } catch (err) {
      newAlert(err instanceof Error ? err.message : 'The set could not be imported.', 'danger')
    } finally {
      setImporting('')
    }
  }

  const onEdit = async (id: number) => {
    try {
      const res = await editCmdCategory(id)
      const row = res.category ?? {}
      setForm({
        name: str(row, 'category_name'),
        os: str(row, 'os_families'),
        role: str(row, 'device_roles'),
        icon: str(row, 'icon'),
        note: str(row, 'note'),
        order: String(num(row, 'category_order')),
      })
      setEditing(id)
    } catch (err) {
      newAlert(err instanceof Error ? err.message : 'Error', 'danger')
    }
  }

  const onRemove = async (id: number) => {
    if (!confirm('Are you sure ?')) return
    try {
      await removeCmdCategory(id)
      await refresh()
    } catch (err) {
      newAlert(err instanceof Error ? err.message : 'Error', 'danger')
    }
  }

  const rows = table.data.data ?? []

  return (
    <div id="content">
      <SettingsHead
        strong="Command Prompt"
        rest="commands categories"
        subtitle="The sections shown to the right of the terminal, on a device. A category appears only on the machines it is marked for."
        tools={
          <>
            <button
              type="button"
              className="btn btn-default btn-sm import-builtins"
              data-os="linux"
              disabled={importing !== ''}
              title="Bring the built-in Linux sections into the catalogue"
              onClick={() => void onImport('linux')}
            >
              <i className="fab fa-linux" aria-hidden="true"></i> Import Linux set
            </button>
            <button
              type="button"
              className="btn btn-default btn-sm import-builtins"
              data-os="windows"
              disabled={importing !== ''}
              title="Bring the built-in Windows sections into the catalogue"
              onClick={() => void onImport('windows')}
            >
              <i className="fab fa-windows" aria-hidden="true"></i> Import Windows set
            </button>
            <button
              type="button"
              className="pull-right btn btn-success btn-sm add-category-btn"
              onClick={() => {
                setForm(EMPTY_CATEGORY)
                setOpen(true)
              }}
            >
              <i className="fas fa-plus-circle" aria-hidden="true"></i> Add category
            </button>
          </>
        }
      />
      <table className="table table-striped table-hover table-bordered settingsDataTable" width="100%" id="cmd-categories">
        <colgroup>
          <col />
          <col style={{ width: 160 }} />
          <col style={{ width: 200 }} />
          <col style={{ width: 80 }} />
          <col style={{ width: 150 }} />
        </colgroup>
        <thead>
          <tr>
            <th className="metroTd a-left">Category</th>
            <th className="metroTd center">Runs on</th>
            <th className="metroTd center">Machine role</th>
            <th className="metroTd center">Order</th>
            <th className="metroTd center">Action</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr>
              <td colSpan={5} className="dataTables_empty">
                No data available in table
              </td>
            </tr>
          )}
          {rows.map((row) => {
            const id = num(row, 'id') || num(row, 'ID')
            const note = str(row, 'note')
            return (
              <tr key={id} id={'row_' + id}>
                <td>
                  {str(row, 'icon') !== '' && (
                    <>
                      <i className={str(row, 'icon')} aria-hidden="true"></i>{' '}
                    </>
                  )}
                  <strong>{str(row, 'category_name')}</strong>
                  {note !== '' && <p className="help-block">{note}</p>}
                </td>
                <td className="center">{labelFor(OS_FAMILIES, str(row, 'os_families'))}</td>
                <td className="center">{labelFor(DEVICE_ROLES, str(row, 'device_roles'))}</td>
                <td className="center">{num(row, 'category_order')}</td>
                <td className="center">
                  <button type="button" className="btn btn-xs btn-primary" onClick={() => void onEdit(id)}>
                    Edit
                  </button>{' '}
                  <button type="button" className="btn btn-xs btn-danger remove-category" data-cat={id} onClick={() => void onRemove(id)}>
                    Remove
                  </button>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>

      <Modal
        id="categoryModal"
        open={open}
        title="New category"
        onClose={() => setOpen(false)}
        footer={
          <>
            <button type="button" className="btn btn-default" onClick={() => setOpen(false)}>
              Close
            </button>
            <button type="button" className="btn btn-primary saveCategory" onClick={() => void onSave()}>
              Save
            </button>
          </>
        }
      >
        <CmdCategoryForm id="newCategory" value={form} onChange={setForm} />
      </Modal>

      <Modal
        id="updateCategoryModal"
        open={editing > 0}
        title="Update category"
        onClose={() => setEditing(0)}
        footer={
          <>
            <button type="button" className="btn btn-default" onClick={() => setEditing(0)}>
              Close
            </button>
            <button type="button" className="btn btn-primary saveEditCategory" onClick={() => void onUpdate()}>
              Save
            </button>
          </>
        }
      >
        <CmdCategoryForm id="editCategory" value={form} onChange={setForm} />
      </Modal>
    </div>
  )
}

function CmdTypesPanel() {
  const query = useQueryClient()
  const table = useQuery(cmdTypesTableQuery())
  const [open, setOpen] = useState(false)
  const [editing, setEditing] = useState(0)
  const [name, setName] = useState('')
  if (table.error instanceof ApiError && table.error.status === 403) return <Forbidden />
  if (!table.data) return <Spinner />

  const refresh = () => query.invalidateQueries({ queryKey: ['settings', 'opteemum', 'cmd-types'] })

  const onSave = async () => {
    try {
      await createCmdCommandType(name)
      toast('A new command type created', 'Command Types', 'success')
      setOpen(false)
      setName('')
      await refresh()
    } catch (err) {
      newAlert(err instanceof Error ? err.message : 'Error', 'danger')
    }
  }

  const onEdit = async (id: number) => {
    try {
      const res = await editCmdCommandType(id)
      setName(str(res.type ?? {}, 'type_name'))
      setEditing(id)
    } catch (err) {
      newAlert(err instanceof Error ? err.message : 'Error', 'danger')
    }
  }

  const onUpdate = async () => {
    try {
      /* `type_name` si `type`, exact campurile lui `editTypeForm`. */
      await updateCmdCommandType({ type_name: name, type: editing })
      toast('Command type updated', 'Command Types', 'success')
      setEditing(0)
      setName('')
      await refresh()
    } catch (err) {
      newAlert(err instanceof Error ? err.message : 'Error', 'danger')
    }
  }

  const onRemove = async (id: number) => {
    if (!confirm('Are you sure ?')) return
    try {
      await removeCmdCommandType(id)
      await refresh()
    } catch (err) {
      newAlert(err instanceof Error ? err.message : 'Error', 'danger')
    }
  }

  const rows = table.data.data ?? []

  return (
    <div id="content">
      <SettingsHead
        strong="Command Prompt"
        rest="command types"
        subtitle="The list of command types. Every command in the catalogue is given one."
        tools={
          <button
            type="button"
            className="pull-right btn btn-success btn-sm add-type-btn"
            onClick={() => {
              setName('')
              setOpen(true)
            }}
          >
            <i className="fas fa-plus-circle" aria-hidden="true"></i> Add command type
          </button>
        }
      />
      {/* Coloanele lui `CmdCommandsTypes`: numarul, tipul, actiunile. */}
      <table className="table table-striped table-hover table-bordered settingsDataTable" width="100%" id="cmd-command-types">
        <colgroup>
          <col style={{ width: 60 }} />
          <col />
          <col style={{ width: 200 }} />
        </colgroup>
        <thead>
          <tr>
            <th className="metroTd center">#</th>
            <th className="metroTd a-left">Type</th>
            <th className="metroTd center">Action</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr>
              <td colSpan={3} className="dataTables_empty">
                No data available in table
              </td>
            </tr>
          )}
          {rows.map((row) => {
            const id = num(row, 'ID') || num(row, 'id')
            return (
              <tr key={id} id={'row_' + id}>
                <td className="center">{id}</td>
                <td>
                  <strong>{str(row, 'type_name')}</strong>
                </td>
                <td className="center">
                  <button type="button" className="btn btn-xs btn-primary" onClick={() => void onEdit(id)}>
                    Edit
                  </button>{' '}
                  <button type="button" className="btn btn-xs btn-danger remove-type" data-type={id} onClick={() => void onRemove(id)}>
                    Remove
                  </button>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>

      <Modal
        id="typeModal"
        open={open}
        title="New command type"
        onClose={() => setOpen(false)}
        footer={
          <>
            <button type="button" className="btn btn-default" onClick={() => setOpen(false)}>
              Close
            </button>
            <button type="button" className="btn btn-primary" id="saveNewTypeForm" onClick={() => void onSave()}>
              Save
            </button>
          </>
        }
      >
        <form method="post">
          <div className="form-group">
            <label className="control-label" htmlFor="new-type-name">
              Type name:
            </label>
            <input
              className="form-control"
              id="new-type-name"
              name="type_name"
              type="text"
              value={name}
              onChange={(ev) => setName(ev.target.value)}
            />
          </div>
        </form>
      </Modal>

      <Modal
        id="updateTypeModal"
        open={editing > 0}
        title="Update command type"
        onClose={() => setEditing(0)}
        footer={
          <>
            <button type="button" className="btn btn-default" onClick={() => setEditing(0)}>
              Close
            </button>
            <button type="button" className="btn btn-primary" id="saveEditTypeForm" onClick={() => void onUpdate()}>
              Save
            </button>
          </>
        }
      >
        <form method="post">
          <div className="form-group">
            <label className="control-label" htmlFor="edit-type-name">
              Type name:
            </label>
            <input
              className="form-control"
              id="edit-type-name"
              name="type_name"
              type="text"
              value={name}
              onChange={(ev) => setName(ev.target.value)}
            />
            <input type="hidden" name="type" value={editing} />
          </div>
        </form>
      </Modal>
    </div>
  )
}

/** Felul comenzii, ca in macheta: CMD, BAT, SHELL. */
const CMD_MAIN_TYPES = [
  { value: '1', label: 'CMD' },
  { value: '2', label: 'BAT' },
  { value: '3', label: 'SHELL' },
]

const YES_NO = [
  { value: '0', label: 'No' },
  { value: '1', label: 'Yes' },
]

/**
 * Pe ce masini se aplica - `cmdPlatformLabel` din portal.
 *
 * Rolul masinii e o pastila linistita, nu una colorata: e o INCADRARE, nu o
 * stare. „Changes the machine" ramane chihlimbariu, fiindca chiar e o
 * avertizare - in portal era un `background:#e3c06a` scris in markup; aici e
 * `label-warning`, caruia `od_console.css` ii da tenta deschisa cu scris
 * inchis, deci se si citeste.
 */
function PlatformLabels({ os, roles, action }: { os: string; roles: string; action: boolean }) {
  return (
    <>
      {os.trim() === '' ? (
        <span className="label label-default">Windows + Linux</span>
      ) : (
        <span className="label label-info">{os.replace(/,/g, ' + ')}</span>
      )}
      {roles.trim() !== '' && (
        <>
          {' '}
          <span className="label label-default">{roles.replace(/,/g, ' + ')}</span>
        </>
      )}
      {action && (
        <>
          {' '}
          <span className="label label-warning">changes the machine</span>
        </>
      )}
    </>
  )
}

/**
 * O pastila cu nume, sau linia care spune ca nu e niciunul.
 *
 * NU `label-primary`, cum scria portalul: `od_console.css` nu-l reaseaza, deci
 * in zona cade pe pastila cenusie si numele isi pierde intelesul. `label-info`
 * e perechea pe care zona chiar o imbraca - tenta deschisa, scris inchis.
 */
function NamePill({ name }: { name: string }) {
  if (name === '') return <i className="fas fa-minus font-10" aria-hidden="true"></i>

  return <span className="label label-info">{name}</span>
}

type CmdCommandFields = Record<string, string>

const EMPTY_COMMAND: CmdCommandFields = {
  id: '',
  main_type: '1',
  server_command: '0',
  display_name: '',
  header: '',
  description: '',
  command: '',
  response_agent: '',
  command_actions: '0',
  scheduled: '',
  timeout: '',
  has_parameters: '',
  param_hint: '',
  param_slot: '',
  is_action: '',
  os_families: '',
  device_roles: '',
  category_id: '0',
  subcategory_id: '0',
  command_type: '',
  command_order: '0',
  info: '',
  button_color: '',
}

function CmdCommandForm({
  value,
  onChange,
  categories,
  commandTypes,
}: {
  value: CmdCommandFields
  onChange(next: CmdCommandFields): void
  categories: Array<Record<string, unknown>>
  commandTypes: Array<Record<string, unknown>>
}) {
  const [subs, setSubs] = useState<Array<Record<string, unknown>>>([])
  /* `Record<string, string>`, nu `Partial<...>`: pe un tip cu semnatura de
     index, `Partial` face fiecare valoare `string | undefined`, si atunci
     imprastierea nu mai da un rand intreg de campuri. */
  const set = (patch: Record<string, string>) => onChange({ ...value, ...patch })
  const category = Number(value.category_id) || 0

  /* Subcategoriile vin pentru categoria aleasa, ca `changeCategory` din portal. */
  useEffect(() => {
    if (category < 1) {
      setSubs([])
      return
    }
    let alive = true
    void cmdSubcategories(category)
      .then((res) => {
        if (alive) setSubs(res.subcategories ?? [])
      })
      .catch(() => {
        if (alive) setSubs([])
      })
    return () => {
      alive = false
    }
  }, [category])

  const text = (name: string, label: string, placeholder?: string) => (
    <div className="form-group">
      <label className="control-label">{label}</label>
      <input
        className="form-control"
        name={name}
        type="text"
        placeholder={placeholder}
        value={value[name] ?? ''}
        onChange={(ev) => set({ [name]: ev.target.value })}
      />
    </div>
  )

  const number = (name: string, label: string) => (
    <div className="form-group">
      <label className="control-label">{label}</label>
      <input
        className="form-control"
        name={name}
        type="number"
        min={0}
        value={value[name] ?? ''}
        onChange={(ev) => set({ [name]: ev.target.value })}
      />
    </div>
  )

  const area = (name: string, label: string, maxLength?: number) => (
    <div className="form-group">
      <label className="control-label">{label}</label>
      <textarea
        className="form-control"
        style={{ maxWidth: '100%' }}
        name={name}
        maxLength={maxLength}
        value={value[name] ?? ''}
        onChange={(ev) => set({ [name]: ev.target.value })}
      />
    </div>
  )

  return (
    <form method="post">
      <div className="form-group">
        <label className="control-label">Main command type:</label>
        <SelectPicker
          id="cmd-main-type"
          className="form-control"
          name="main_type"
          title="CMD"
          value={value.main_type ?? '1'}
          onChange={(next) => set({ main_type: next })}
          options={CMD_MAIN_TYPES}
        />
      </div>
      <div className="form-group">
        <label className="control-label">Server command:</label>
        <SelectPicker
          id="cmd-server-command"
          className="form-control"
          name="server_command"
          title="No"
          value={value.server_command ?? '0'}
          onChange={(next) => set({ server_command: next })}
          options={YES_NO}
        />
      </div>
      {text('display_name', 'Display name:')}
      {text('header', 'Header:')}
      {text('description', 'Description:')}
      {area('command', 'Command:')}
      {area('response_agent', 'Response Agent:', 16)}
      {number('command_actions', 'Number of actions:')}
      {text('scheduled', 'Scheduled:')}
      {number('timeout', 'Timeout (seconds):')}

      <div className="form-group">
        <SettingsCheck
          label="Parameters field"
          name="has_parameters"
          checked={value.has_parameters === '1'}
          onChange={(on) => set({ has_parameters: on ? '1' : '' })}
        />
      </div>
      {text('param_hint', 'Parameter hint:', 'unit name')}
      <p className="help-block">What the box asks for when the button is clicked. Only used with a parameters field.</p>
      {text('param_slot', 'Parameter placement:', 'PLACEHOLDER')}
      <p className="help-block">
        Left empty, the value goes at the end of the command. Write a mark here and the same mark inside the command, and the
        value takes its place instead.
      </p>
      <div className="form-group">
        <SettingsCheck
          label="Changes the machine"
          name="is_action"
          checked={value.is_action === '1'}
          onChange={(on) => set({ is_action: on ? '1' : '' })}
        />
        <p className="help-block">
          A restart, a kill, a clean-up. The button turns yellow, so it is not pressed by mistake next to one that only reads.
        </p>
      </div>

      <div className="form-group">
        <label className="control-label">Runs on:</label>
        <SelectPicker
          id="cmd-os-families"
          className="form-control"
          name="os_families"
          title="Windows and Linux"
          value={value.os_families ?? ''}
          onChange={(next) => set({ os_families: next })}
          options={withStored(OS_FAMILIES, value.os_families ?? '')}
        />
      </div>
      <div className="form-group">
        <label className="control-label">Machine role:</label>
        <SelectPicker
          id="cmd-device-roles"
          className="form-control"
          name="device_roles"
          title="Servers and workstations"
          value={value.device_roles ?? ''}
          onChange={(next) => set({ device_roles: next })}
          options={withStored(DEVICE_ROLES, value.device_roles ?? '')}
        />
      </div>

      <div className="form-group">
        <label className="control-label">Category:</label>
        <SelectPicker
          id="cmd-category"
          className="form-control"
          name="category_id"
          title="Select category..."
          value={value.category_id ?? '0'}
          onChange={(next) => set({ category_id: next, subcategory_id: '0' })}
          options={[
            { value: '0', label: 'Select category...' },
            ...categories.map((c) => ({ value: String(num(c, 'id') || num(c, 'ID')), label: str(c, 'category_name') })),
          ]}
        />
      </div>
      <div className="form-group">
        <label className="control-label">Subcategory:</label>
        <SelectPicker
          id="cmd-subcategory"
          className="form-control"
          name="subcategory_id"
          title={subs.length === 0 ? 'No subcategories' : 'Select subcategory...'}
          value={value.subcategory_id ?? '0'}
          onChange={(next) => set({ subcategory_id: next })}
          options={[
            { value: '0', label: subs.length === 0 ? 'No subcategories' : 'Select subcategory...' },
            ...subs.map((s) => ({ value: String(num(s, 'id') || num(s, 'ID')), label: str(s, 'subcategory_name') })),
          ]}
        />
      </div>
      <div className="form-group">
        <label className="control-label">Command type:</label>
        <SelectPicker
          id="cmd-command-type"
          className="form-control"
          name="command_type"
          title="Select type..."
          value={value.command_type ?? ''}
          onChange={(next) => set({ command_type: next })}
          options={[
            { value: '', label: 'Select type...' },
            ...commandTypes.map((t) => ({ value: String(num(t, 'ID') || num(t, 'id')), label: str(t, 'type_name') })),
          ]}
        />
      </div>
      {number('command_order', 'Command order:')}
      {area('info', 'Info:')}
      {text('button_color', 'Button color:', '#0000ff')}
    </form>
  )
}

function CmdCommandsPanel() {
  const query = useQueryClient()
  const page = useQuery(cmdCommandsQuery())
  const table = useQuery(cmdCommandsTableQuery())
  const [open, setOpen] = useState(false)
  const [editing, setEditing] = useState(false)
  const [form, setForm] = useState<CmdCommandFields>(EMPTY_COMMAND)
  if (page.error instanceof ApiError && page.error.status === 403) return <Forbidden />
  if (!page.data || !table.data) return <Spinner />

  const refresh = () => query.invalidateQueries({ queryKey: ['settings', 'opteemum', 'cmd-commands-table'] })

  const onSave = async () => {
    try {
      if (editing) {
        await updateCmdCommand(form)
        toast('Command updated', 'CMD Commands', 'success')
      } else {
        await createCmdCommand(form)
        toast('A new command created', 'CMD Commands', 'success')
      }
      setOpen(false)
      setEditing(false)
      setForm(EMPTY_COMMAND)
      await refresh()
    } catch (err) {
      newAlert(err instanceof Error ? err.message : 'Error', 'danger')
    }
  }

  /* Randul are deja tot ce cere formularul - portalul il lua tot din `data`
     al randului, nu dintr-o a doua cerere. */
  const onEdit = (row: Record<string, unknown>) => {
    const next: CmdCommandFields = { ...EMPTY_COMMAND }
    for (const key of Object.keys(EMPTY_COMMAND)) {
      const raw = row[key] ?? row[key.toLowerCase()]
      next[key] = raw === null || raw === undefined ? EMPTY_COMMAND[key] : String(raw)
    }
    next.id = String(num(row, 'id') || num(row, 'ID'))
    setForm(next)
    setEditing(true)
    setOpen(true)
  }

  const onRemove = async (id: number) => {
    if (!confirm('Are you sure ?')) return
    try {
      await removeCmdCommand(id)
      await refresh()
    } catch (err) {
      newAlert(err instanceof Error ? err.message : 'Error', 'danger')
    }
  }

  const rows = table.data.data ?? []

  return (
    <div id="content">
      <SettingsHead
        strong="Command Prompt"
        rest="commands"
        subtitle="The buttons offered on a device, next to the terminal. Each one belongs to a section and runs on the machines it is marked for."
        tools={
          <button
            type="button"
            className="pull-right btn btn-success btn-sm add-command-btn"
            onClick={() => {
              setForm(EMPTY_COMMAND)
              setEditing(false)
              setOpen(true)
            }}
          >
            <i className="fas fa-plus-circle" aria-hidden="true"></i> Add command
          </button>
        }
      />
      <table className="table table-striped table-hover table-bordered settingsDataTable" width="100%" id="cmd-commands">
        <colgroup>
          <col />
          <col style={{ width: 130 }} />
          <col style={{ width: 130 }} />
          <col style={{ width: 90 }} />
          <col style={{ width: 130 }} />
          <col style={{ width: 230 }} />
          <col style={{ width: 90 }} />
          <col style={{ width: 150 }} />
        </colgroup>
        <thead>
          <tr>
            <th className="metroTd a-left">Command display</th>
            <th className="metroTd center">Category</th>
            <th className="metroTd center">Subcategory</th>
            <th className="metroTd center">Parameters</th>
            <th className="metroTd center">Command type</th>
            <th className="metroTd a-left">Runs on</th>
            <th className="metroTd center">Server</th>
            <th className="metroTd center">Action</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr>
              <td colSpan={8} className="dataTables_empty">
                No data available in table
              </td>
            </tr>
          )}
          {rows.map((row) => {
            const id = num(row, 'id') || num(row, 'ID')
            const command = str(row, 'command')
            return (
              <tr key={id} id={'row_' + id}>
                <td>
                  <strong>{str(row, 'display_name')}</strong>
                  {command !== '' && <p className="help-block">{command}</p>}
                </td>
                <td className="center">
                  <NamePill name={str(row, 'category_name')} />
                </td>
                <td className="center">
                  <NamePill name={str(row, 'subcategory_name')} />
                </td>
                <td className="center">
                  {num(row, 'has_parameters') === 1 ? (
                    <i className="fas fa-check-circle" aria-label="yes"></i>
                  ) : (
                    <i className="fas fa-times-circle" aria-label="no"></i>
                  )}
                </td>
                <td className="center">
                  <NamePill name={str(row, 'type_name')} />
                </td>
                <td>
                  <PlatformLabels
                    os={str(row, 'os_families')}
                    roles={str(row, 'device_roles')}
                    action={num(row, 'is_action') === 1}
                  />
                </td>
                <td className="center">
                  {num(row, 'server_command') === 1 ? (
                    <span className="label label-info">Yes</span>
                  ) : (
                    <span className="label label-default">No</span>
                  )}
                </td>
                <td className="center">
                  <button type="button" className="btn btn-xs btn-primary edit-command" onClick={() => onEdit(row)}>
                    Edit
                  </button>{' '}
                  <button type="button" className="btn btn-xs btn-danger remove-command" data-cmd={id} onClick={() => void onRemove(id)}>
                    Delete
                  </button>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>

      <Modal
        id="commandModal"
        open={open}
        large
        title={editing ? 'Update command' : 'New command'}
        onClose={() => setOpen(false)}
        footer={
          <>
            <button type="button" className="btn btn-default" onClick={() => setOpen(false)}>
              Close
            </button>
            <button type="button" className="btn btn-primary" id="saveNewCommandForm" onClick={() => void onSave()}>
              Save
            </button>
          </>
        }
      >
        <CmdCommandForm
          value={form}
          onChange={setForm}
          categories={page.data.categories ?? []}
          commandTypes={page.data.commandTypes ?? []}
        />
      </Modal>
    </div>
  )
}

type StandardFields = { ID: string; command: string; command_title: string; category_id: string; type: string; info: string; message: string }

const EMPTY_STANDARD: StandardFields = { ID: '', command: '', command_title: '', category_id: '', type: '', info: '', message: '' }

function StandardCommandsPanel() {
  const query = useQueryClient()
  const page = useQuery(standardCommandsQuery())
  const table = useQuery(standardCommandsTableQuery())
  const [open, setOpen] = useState(false)
  const [editing, setEditing] = useState(false)
  const [form, setForm] = useState<StandardFields>(EMPTY_STANDARD)
  if (page.error instanceof ApiError && page.error.status === 403) return <Forbidden />
  if (!page.data || !table.data) return <Spinner />

  const refresh = () => query.invalidateQueries({ queryKey: ['settings', 'opteemum', 'standard-table'] })
  const set = (patch: Partial<StandardFields>) => setForm({ ...form, ...patch })

  const onSave = async () => {
    try {
      if (editing) {
        await updateStandardCommand({ ...form })
        toast('Command updated', 'Standard Commands', 'success')
      } else {
        await createStandardCommand({ ...form })
        toast('A new command created', 'Standard Commands', 'success')
      }
      setOpen(false)
      setEditing(false)
      setForm(EMPTY_STANDARD)
      await refresh()
    } catch (err) {
      newAlert(err instanceof Error ? err.message : 'Error', 'danger')
    }
  }

  const onEdit = (row: Record<string, unknown>) => {
    setForm({
      ID: String(num(row, 'ID') || num(row, 'id')),
      command: str(row, 'command'),
      command_title: str(row, 'command_title'),
      category_id: String(num(row, 'category_id') || ''),
      type: str(row, 'type'),
      info: str(row, 'info'),
      message: str(row, 'message'),
    })
    setEditing(true)
    setOpen(true)
  }

  const onRemove = async (id: number) => {
    if (!confirm('Are you sure ?')) return
    try {
      await removeStandardCommand(id)
      await refresh()
    } catch (err) {
      newAlert(err instanceof Error ? err.message : 'Error', 'danger')
    }
  }

  const rows = table.data.data ?? []

  return (
    <div id="content">
      <SettingsHead
        strong="Standard"
        rest="commands"
        subtitle="The ready-made commands of this company. Each one can carry a category and a type."
        tools={
          <button
            type="button"
            className="pull-right btn btn-success btn-sm add-command-btn"
            onClick={() => {
              setForm(EMPTY_STANDARD)
              setEditing(false)
              setOpen(true)
            }}
          >
            <i className="fas fa-plus-circle" aria-hidden="true"></i> Add command
          </button>
        }
      />
      <table className="table table-striped table-hover table-bordered settingsDataTable" width="100%" id="standard-commands">
        <colgroup>
          <col />
          <col style={{ width: 280 }} />
          <col style={{ width: 150 }} />
          <col style={{ width: 150 }} />
          <col style={{ width: 150 }} />
        </colgroup>
        <thead>
          <tr>
            <th className="metroTd a-left">Command</th>
            <th className="metroTd a-left">Command title</th>
            <th className="metroTd center">Category</th>
            <th className="metroTd center">Command type</th>
            <th className="metroTd center">Action</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr>
              <td colSpan={5} className="dataTables_empty">
                No data available in table
              </td>
            </tr>
          )}
          {rows.map((row) => {
            const id = num(row, 'ID') || num(row, 'id')
            return (
              <tr key={id} id={'row_' + id}>
                <td>{str(row, 'command')}</td>
                <td>
                  <strong>{str(row, 'command_title')}</strong>
                </td>
                <td className="center">
                  <NamePill name={str(row, 'category_name')} />
                </td>
                <td className="center">
                  <NamePill name={str(row, 'type_name')} />
                </td>
                <td className="center">
                  <button type="button" className="btn btn-xs btn-primary edit-command" onClick={() => onEdit(row)}>
                    Edit
                  </button>{' '}
                  <button type="button" className="btn btn-xs btn-danger remove-command" data-scmd={id} onClick={() => void onRemove(id)}>
                    Delete
                  </button>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>

      <Modal
        id="commandModal"
        open={open}
        title={editing ? 'Update command' : 'New command'}
        onClose={() => setOpen(false)}
        footer={
          <>
            <button type="button" className="btn btn-default" onClick={() => setOpen(false)}>
              Close
            </button>
            <button type="button" className="btn btn-primary" id="saveNewCommandForm" onClick={() => void onSave()}>
              Save
            </button>
          </>
        }
      >
        <form method="post">
          {editing && <input type="hidden" name="ID" value={form.ID} />}
          <div className="form-group">
            <label className="control-label">Command:</label>
            <input className="form-control" name="command" type="text" value={form.command} onChange={(ev) => set({ command: ev.target.value })} />
          </div>
          <div className="form-group">
            <label className="control-label">Command title:</label>
            <input
              className="form-control"
              name="command_title"
              type="text"
              value={form.command_title}
              onChange={(ev) => set({ command_title: ev.target.value })}
            />
          </div>
          <div className="form-group">
            <label className="control-label">Category:</label>
            <SelectPicker
              id="standard-category"
              className="form-control"
              name="category_id"
              title="Select category..."
              value={form.category_id}
              onChange={(next) => set({ category_id: next })}
              options={[
                { value: '', label: 'Select category...' },
                ...(page.data.categories ?? []).map((c) => ({
                  value: String(num(c, 'ID') || num(c, 'id')),
                  label: str(c, 'category_name'),
                })),
              ]}
            />
          </div>
          <div className="form-group">
            <label className="control-label">Command type:</label>
            <SelectPicker
              id="standard-type"
              className="form-control"
              name="type"
              title="Select type..."
              value={form.type}
              onChange={(next) => set({ type: next })}
              options={[
                { value: '', label: 'Select type...' },
                ...(page.data.commandTypes ?? []).map((t) => ({
                  value: String(num(t, 'ID') || num(t, 'id')),
                  label: str(t, 'type_name'),
                })),
              ]}
            />
          </div>
          <div className="form-group">
            <label className="control-label">Info:</label>
            <textarea className="form-control" style={{ maxWidth: '100%' }} name="info" value={form.info} onChange={(ev) => set({ info: ev.target.value })} />
          </div>
          <div className="form-group">
            <label className="control-label">Message:</label>
            <textarea
              className="form-control"
              style={{ maxWidth: '100%' }}
              name="message"
              value={form.message}
              onChange={(ev) => set({ message: ev.target.value })}
            />
          </div>
        </form>
      </Modal>
    </div>
  )
}

/** Felul comenzii de agent, ca in `agentCommandsListModel`. */
const AGENT_TYPES = [
  { value: '1', label: 'Automated', pill: 'label-success' },
  { value: '2', label: 'Manual', pill: 'label-info' },
]

const AGENT_CHECKED = [
  { value: '0', label: 'Unchecked' },
  { value: '1', label: 'Checked' },
]

type AgentFields = {
  command: string
  command_title: string
  type: string
  group_id: string
  checked: string
  description: string
  message: string
  info: string
}

const EMPTY_AGENT: AgentFields = {
  command: '',
  command_title: '',
  type: '',
  group_id: '',
  checked: '',
  description: '',
  message: '',
  info: '',
}

function AgentCommandsPanel() {
  const query = useQueryClient()
  const page = useQuery(agentCommandsQuery())
  const table = useQuery(agentCommandsTableQuery())
  const [open, setOpen] = useState(false)
  const [editing, setEditing] = useState(0)
  const [form, setForm] = useState<AgentFields>(EMPTY_AGENT)
  if (page.error instanceof ApiError && page.error.status === 403) return <Forbidden />
  if (!page.data || !table.data) return <Spinner />

  const refresh = () => query.invalidateQueries({ queryKey: ['settings', 'opteemum', 'agent-commands-table'] })
  const set = (patch: Partial<AgentFields>) => setForm({ ...form, ...patch })

  /*
   * Capetele astea raspund cu `status`, nu cu o eroare de retea: un camp lipsa
   * intoarce 200 si `status: 'ERROR'`. Panoul de dinainte nu se uita deloc la
   * el si spunea „Command saved." pentru o salvare care nu avusese loc.
   */
  const onSave = async () => {
    try {
      const res = editing > 0 ? await saveEditAgentCommand(editing, { ...form }) : await insertAgentCommand({ ...form })
      if (res.status !== 'OK') {
        newAlert(res.message ?? 'Something went wrong. Please try again', 'danger')
        return
      }
      toast(res.message ?? 'Command saved', 'Agent Commands', 'success')
      setOpen(false)
      setEditing(0)
      setForm(EMPTY_AGENT)
      await refresh()
    } catch (err) {
      newAlert(err instanceof Error ? err.message : 'Error', 'danger')
    }
  }

  const onEdit = async (id: number) => {
    try {
      const res = await editAgentCommand(id)
      const row = res.get_command ?? {}
      setForm({
        command: str(row, 'command'),
        command_title: str(row, 'command_title'),
        type: str(row, 'type'),
        group_id: String(num(row, 'group_id') || ''),
        checked: str(row, 'checked'),
        description: str(row, 'description'),
        message: str(row, 'message'),
        info: str(row, 'info'),
      })
      setEditing(id)
      setOpen(true)
    } catch (err) {
      newAlert(err instanceof Error ? err.message : 'Error', 'danger')
    }
  }

  const groupOptions = (page.data.get_groups ?? []).map((g) => ({
    value: String(num(g, 'ID')),
    label: str(g, 'group_name'),
  }))
  const rows = table.data.data ?? []

  return (
    <div id="content">
      <SettingsHead
        strong="Commands"
        rest="list"
        subtitle="Manage, create or edit the commands the agent offers."
        tools={
          <button
            type="button"
            className="pull-right btn btn-success btn-sm add-command-btn"
            onClick={() => {
              setForm(EMPTY_AGENT)
              setEditing(0)
              setOpen(true)
            }}
          >
            <i className="fas fa-plus-circle" aria-hidden="true"></i> Create Command
          </button>
        }
      />
      <table className="table table-striped table-hover table-bordered settingsDataTable" width="100%" id="commands-list">
        <colgroup>
          <col />
          <col style={{ width: 220 }} />
          <col style={{ width: 140 }} />
          <col style={{ width: 120 }} />
          <col style={{ width: 190 }} />
          <col style={{ width: 190 }} />
          <col style={{ width: 90 }} />
        </colgroup>
        <thead>
          <tr>
            <th className="metroTd a-left">Command</th>
            <th className="metroTd a-left">Command title</th>
            <th className="metroTd center">Group name</th>
            <th className="metroTd center">Command type</th>
            <th className="metroTd a-left">Description</th>
            <th className="metroTd a-left">Message</th>
            <th className="metroTd center">Action</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr>
              <td colSpan={7} className="dataTables_empty">
                No data available in table
              </td>
            </tr>
          )}
          {rows.map((row) => {
            const id = num(row, 'ID') || num(row, 'id')
            const kind = AGENT_TYPES.find((t) => t.value === str(row, 'type'))
            return (
              <tr key={id} id={'row_' + id}>
                <td>{str(row, 'command')}</td>
                <td>
                  <strong>{str(row, 'command_title')}</strong>
                </td>
                <td className="center">
                  <NamePill name={str(row, 'group_name')} />
                </td>
                <td className="center">
                  {kind ? (
                    <span className={`label ${kind.pill}`}>{kind.label}</span>
                  ) : (
                    <i className="fas fa-minus font-10" aria-hidden="true"></i>
                  )}
                </td>
                <td>{str(row, 'description')}</td>
                <td>{str(row, 'message')}</td>
                <td className="center">
                  <button type="button" className="btn btn-xs btn-primary show_details" data-id={id} onClick={() => void onEdit(id)}>
                    <i className="fas fa-pencil-alt" aria-hidden="true"></i> Edit
                  </button>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>

      <Modal
        id="newCommandModal"
        open={open}
        large
        title={editing > 0 ? 'Edit Command' : 'Add New Command'}
        onClose={() => setOpen(false)}
        footer={
          <>
            <button type="button" className="btn btn-default" onClick={() => setOpen(false)}>
              Close
            </button>
            <button type="button" className="btn btn-success" id="saveagentinsertcommand" onClick={() => void onSave()}>
              Save
            </button>
          </>
        }
      >
        <form method="post">
          <div className="form-group">
            <label className="control-label">
              Command <span className="required">*</span>
            </label>
            <input className="form-control" name="command" type="text" value={form.command} onChange={(ev) => set({ command: ev.target.value })} />
          </div>
          <div className="form-group">
            <label className="control-label">
              Command Title <span className="required">*</span>
            </label>
            <input
              className="form-control"
              name="command_title"
              type="text"
              value={form.command_title}
              onChange={(ev) => set({ command_title: ev.target.value })}
            />
          </div>
          <div className="form-group">
            <label className="control-label">
              Command Type <span className="required">*</span>
            </label>
            <SelectPicker
              id="agent-type"
              className="form-control"
              name="type"
              title=" - choose - "
              value={form.type}
              onChange={(next) => set({ type: next })}
              options={[{ value: '', label: ' - choose - ' }, ...AGENT_TYPES.map((t) => ({ value: t.value, label: t.label }))]}
            />
          </div>
          <div className="form-group">
            <label className="control-label">
              Assign to Group <span className="required">*</span>
            </label>
            <SelectPicker
              id="agent-group"
              className="form-control"
              name="group_id"
              title=" - choose - "
              value={form.group_id}
              onChange={(next) => set({ group_id: next })}
              options={[{ value: '', label: ' - choose - ' }, ...groupOptions]}
            />
          </div>
          <div className="form-group">
            <label className="control-label">
              Checked <span className="required">*</span>
            </label>
            <SelectPicker
              id="agent-checked"
              className="form-control"
              name="checked"
              title=" - choose - "
              value={form.checked}
              onChange={(next) => set({ checked: next })}
              options={[{ value: '', label: ' - choose - ' }, ...AGENT_CHECKED]}
            />
          </div>
          <div className="form-group">
            <label className="control-label">Description</label>
            <textarea
              className="form-control"
              style={{ maxWidth: '100%' }}
              name="description"
              value={form.description}
              onChange={(ev) => set({ description: ev.target.value })}
            />
          </div>
          <div className="form-group">
            <label className="control-label">Message</label>
            <textarea
              className="form-control"
              style={{ maxWidth: '100%' }}
              name="message"
              value={form.message}
              onChange={(ev) => set({ message: ev.target.value })}
            />
          </div>
          <div className="form-group">
            <label className="control-label">Info</label>
            <textarea
              className="form-control"
              style={{ maxWidth: '100%' }}
              name="info"
              value={form.info}
              onChange={(ev) => set({ info: ev.target.value })}
            />
          </div>
        </form>
      </Modal>
    </div>
  )
}

/**
 * MACOS Commands > Commands List.
 *
 * Capul zonei, lista pe pagini si fereastra consolei - ca pe ecranele surori
 * din Settings > Softsee. Inainte: `h4` fara subtitlu, butonul verde cazut SUB
 * linie, lista fara cautare, fara numar de randuri si fara paginare, si doua
 * coloane cu capul scris cu litere mici (`name`, `command`), adica numele
 * campurilor din baza aratate omului.
 *
 * Lista vine PE PAGINI: `/settings/opteemum/macos-commands/table` citea de mult
 * `start`, `length` si `search` (`DbalOpteemumSettingsSource::macosCommandsTable`)
 * si raspundea cu `recordsFiltered` - numai clientul cerea o pagina fixa de 200
 * si arunca numaratoarea.
 *
 * `SoftseeListChrome` se IMPORTA, nu se copiaza: e markupul lui DataTables 1.10
 * pentru un panou care sta langa capul lui de zona, si a doua copie a lui s-ar
 * desparti de prima la prima schimbare.
 */
function MacosCommandsPanel() {
  const query = useQueryClient()
  const [start, setStart] = useState(0)
  const [length, setLength] = useState(25)
  const [search, setSearch] = useState('')
  /* `keepPreviousData`: pagina de acum ramane pe ecran pana vine urmatoarea. */
  const table = useQuery({
    ...macosCommandsTableQuery({ start, length, search }),
    placeholderData: keepPreviousData,
  })
  const [open, setOpen] = useState(false)
  /* Sertarul consolei e controlat, deci valoarea lui sta aici. `<select>`-ul
     ascuns ramane in formular, deci `FormData` pleaca neschimbat. */
  const [active, setActive] = useState('1')
  if (table.error instanceof ApiError && table.error.status === 403) return <Forbidden />
  if (table.isPending) return <Spinner />
  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const form = new FormData(event.currentTarget)
    try {
      await insertMacosCommand({
        name: String(form.get('name') ?? ''),
        command: String(form.get('command') ?? ''),
        active: String(form.get('active') ?? '1'),
      })
      toast('Command saved.', '', 'success')
      setOpen(false)
      setActive('1')
      await query.invalidateQueries({ queryKey: ['settings', 'opteemum', 'macos-table'] })
    } catch (err) {
      newAlert(err instanceof Error ? err.message : 'Error', 'danger')
    }
  }
  const rows = table.data?.data ?? []
  return (
    <>
      <div id="content">
        <SettingsHead
          strong="MACOS Commands"
          rest="list"
          subtitle={
            <>
              Manage, create or edit <strong>commands list</strong>
            </>
          }
          tools={
            <button type="button" className="pull-right btn btn-success btn-sm" onClick={() => setOpen(true)}>
              <i className="fas fa-plus-circle" aria-hidden="true"></i> Add command
            </button>
          }
        />
        <SoftseeListChrome
          id="mac-commands-list"
          search={search}
          onSearch={(next) => {
            setSearch(next)
            setStart(0)
          }}
          start={start}
          length={length}
          total={table.data?.recordsFiltered ?? 0}
          fetching={table.isFetching}
          onStart={setStart}
          onLength={(next) => {
            setLength(next)
            setStart(0)
          }}
        >
          <table id="mac-commands-list" className="table table-striped table-hover table-bordered dataTableM dataTable no-footer">
            {/* Coloanele au latimea lor, deci starea sta la acelasi x pe toate randurile. */}
            <colgroup>
              <col style={{ width: 240 }} />
              <col />
              <col style={{ width: 300 }} />
              <col style={{ width: 110 }} />
            </colgroup>
            <thead>
              <tr>
                <th className="td-small a-left">Name</th>
                <th className="td-small a-left">Description</th>
                <th className="td-small a-left">Command</th>
                <th className="td-small a-left">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr className="odd">
                  <td colSpan={4} className="dataTables_empty">
                    No data available in table
                  </td>
                </tr>
              ) : (
                rows.map((row) => {
                  const on = num(row, 'active') === 1
                  return (
                    <tr key={num(row, 'ID')} id={'row_' + num(row, 'ID')}>
                      <td className="td-small a-left">{str(row, 'name') || <i className="fas fa-minus font-10"></i>}</td>
                      <td className="td-small a-left">{str(row, 'description') || <i className="fas fa-minus font-10"></i>}</td>
                      <td className="td-small a-left">{str(row, 'command') || <i className="fas fa-minus font-10"></i>}</td>
                      <td className="td-small a-left">
                        {/*
                         * Doua intelesuri, doua culori. `label-success` e verdele
                         * zonei din `od_console.css` (fundal deschis, scris inchis),
                         * `label-default` griul lui Bootstrap pentru "stinsa". In
                         * lista veche nu era nicio pastila: starea nu se vedea deloc,
                         * desi ea hotaraste daca comanda ajunge pe statii.
                         */}
                        <span className={'label ' + (on ? 'label-success' : 'label-default')}>{on ? 'Active' : 'Inactive'}</span>
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </SoftseeListChrome>
      </div>
      {/* Fereastra comuna a zonei: val in spate, `body.modal-open`, Escape. */}
      <Modal
        id="newCommandModal"
        open={open}
        title="Add New Command"
        large
        onClose={() => setOpen(false)}
        footer={
          <>
            <button type="button" className="btn btn-default" onClick={() => setOpen(false)}>
              Close
            </button>
            <button type="submit" form="new-commands-list-mac-form" className="btn btn-primary">
              <i className="far fa-save"></i> Save
            </button>
          </>
        }
      >
        <form
          className="form-horizontal labelcustomize"
          id="new-commands-list-mac-form"
          data-collabel="3"
          data-alignlabel="left"
          data-label="color"
          method="post"
          action="/settings/opteemum/save_insert_macos_command"
          onSubmit={(ev) => void onSubmit(ev)}
        >
          <div className="form-group">
            <label className="col-sm-3 control-label" style={{ textAlign: 'left' }}>
              <span className="color">Command name</span>
            </label>
            <div className="col-sm-9">
              <input className="form-control" name="name" type="text" required />
            </div>
          </div>
          <div className="form-group">
            <label className="col-sm-3 control-label" style={{ textAlign: 'left' }}>
              <span className="color">Command</span>
            </label>
            <div className="col-sm-9">
              <textarea style={{ maxWidth: '100%' }} className="form-control" name="command" rows={7} required></textarea>
            </div>
          </div>
          <div className="form-group">
            <label className="col-sm-3 control-label" style={{ textAlign: 'left' }}>
              <span className="color">Status</span>
            </label>
            <div className="col-sm-9">
              {/* `SelectPicker` CU `form-control` pe invelis, ca celelalte din v4:
                  fara ea sertarul nu tine latimea campului. */}
              <SelectPicker
                id="newMacosCommandActive"
                className="form-control"
                name="active"
                title="Select.."
                value={active}
                onChange={setActive}
                options={[
                  { value: '1', label: 'Active' },
                  { value: '0', label: 'Inactive' },
                ]}
              />
            </div>
          </div>
        </form>
      </Modal>
    </>
  )
}

/**
 * Software > Manage Software.
 *
 * Geamanul lui Settings > Softsee > Manage applications, si acum seamana cu el.
 * Inainte: butonul "Add category" era `btn-block`, lat de 272px, asezat
 * DEASUPRA coloanei din stanga - adica peste lista - iar coloana era
 * `nav-metro-tabs` cu albastrul (65,153,194) scris INLINE pe randul "No
 * category", care se aprindea intotdeauna, oricare ar fi fost randul ales.
 * Sertarele erau `<select>`-uri brute de 508px.
 *
 * Catalogul vine PE PAGINI: categoria, cautarea si pagina pleaca la server.
 * Capatul stia de mult `appType`, `start`, `length` si `search`; clientul cerea
 * o pagina fixa de 200 si nu-i trimitea categoria.
 */
function ApplicationsPanel() {
  const query = useQueryClient()
  const [appType, setAppType] = useState(-1)
  const [start, setStart] = useState(0)
  const [length, setLength] = useState(25)
  const [search, setSearch] = useState('')
  const page = useQuery(applicationsQuery())
  /* `keepPreviousData`: pagina de acum ramane pe ecran pana vine urmatoarea. */
  const table = useQuery({
    ...applicationsTableQuery({ appType, start, length, search }),
    placeholderData: keepPreviousData,
  })
  const [open, setOpen] = useState(false)
  const [name, setName] = useState('')
  if (page.error instanceof ApiError && page.error.status === 403) return <Forbidden />
  if (!page.data) return <Spinner />
  const onSave = async () => {
    try {
      await newCategory(name)
      toast('Category saved.', '', 'success')
      setOpen(false)
      setName('')
      await query.invalidateQueries({ queryKey: ['settings', 'opteemum'] })
    } catch (err) {
      newAlert(err instanceof Error ? err.message : 'Error', 'danger')
    }
  }
  const onChange = async (application_id: number, category: number) => {
    try {
      await changeApplicationCategory(application_id, category)
      await query.invalidateQueries({ queryKey: ['settings', 'opteemum', 'applications-table'] })
    } catch (err) {
      newAlert(err instanceof Error ? err.message : 'Error', 'danger')
    }
  }
  const categoryOptions = [
    { value: '0', label: 'Select category...' },
    ...page.data.app_categories.map((cat) => ({ value: String(num(cat, 'ID')), label: str(cat, 'category_name') })),
  ]
  /* O categorie noua din coloana reia lista de la prima pagina. */
  const pick = (next: number) => {
    setAppType(next)
    setStart(0)
  }
  const rows = table.data?.data ?? []
  return (
    <>
      <div id="content">
        <SettingsHead
          strong="Manage"
          rest="software"
          subtitle="software management system"
          tools={
            <button type="button" id="odAddCategory" className="pull-right btn btn-success btn-sm" onClick={() => setOpen(true)}>
              <i className="fas fa-plus-circle" aria-hidden="true"></i> Add category
            </button>
          }
        />
        <div className="row">
          <div className="col-md-3">
            {/* Coloana de alegere a consolei: randul ALES se aprinde, nu primul. */}
            <SettingsPicker id="categories-list">
              <SettingsPickerItem label="No category" active={appType === 0} onPick={() => pick(0)} liProps={{ 'data-category': 0 }} />
              <SettingsPickerItem label="All categories" active={appType === -1} onPick={() => pick(-1)} liProps={{ 'data-category': -1 }} />
              {page.data.app_categories.map((cat) => (
                <SettingsPickerItem
                  key={num(cat, 'ID')}
                  label={str(cat, 'category_name')}
                  active={appType === num(cat, 'ID')}
                  onPick={() => pick(num(cat, 'ID'))}
                  liProps={{ 'data-category': num(cat, 'ID'), className: 'category-tab' }}
                />
              ))}
            </SettingsPicker>
          </div>
          <div className="col-md-9">
            <SoftseeListChrome
              id="applicationsSettingsTable"
              search={search}
              onSearch={(next) => {
                setSearch(next)
                setStart(0)
              }}
              start={start}
              length={length}
              total={table.data?.recordsFiltered ?? 0}
              fetching={table.isFetching}
              onStart={setStart}
              onLength={(next) => {
                setLength(next)
                setStart(0)
              }}
            >
              <table id="applicationsSettingsTable" className="table table-striped table-hover table-bordered dataTableM dataTable no-footer">
                {/* Coloanele au latimea lor, deci categoria si sertarul stau la acelasi x pe toate randurile. */}
                <colgroup>
                  <col />
                  <col style={{ width: 220 }} />
                  <col style={{ width: 260 }} />
                </colgroup>
                <thead>
                  <tr>
                    <th className="td-small a-left">Application</th>
                    <th className="td-small a-left">Category</th>
                    <th className="td-small a-left">Change category</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr className="odd">
                      <td colSpan={3} className="dataTables_empty">
                        No data available in table
                      </td>
                    </tr>
                  ) : (
                    rows.map((app) => (
                      <tr key={num(app, 'ID')} id={'row_' + num(app, 'ID')}>
                        <td className="td-small a-left">{str(app, 'short_name') || <i className="fas fa-minus font-10"></i>}</td>
                        <td className="td-small a-left">
                          {str(app, 'category_name') ? str(app, 'category_name') : <span className="label label-default">No category</span>}
                        </td>
                        <td className="td-small a-left">
                          <form className="changeCategoryForm" action="/settings/opteemum/changeApplicationCategory" method="post">
                            <input type="hidden" value={num(app, 'ID')} name="application_id" />
                            {/* `SelectPicker` CU `form-control` pe invelis: fara ea sertarul
                                nu tine latimea campului. */}
                            <SelectPicker
                              id={'applicationCategory_' + num(app, 'ID')}
                              className="form-control input-sm applicationCategory"
                              name="category"
                              title="Select category..."
                              value={String(num(app, 'category_id'))}
                              onChange={(value) => void onChange(num(app, 'ID'), Number(value))}
                              options={categoryOptions}
                            />
                          </form>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </SoftseeListChrome>
          </div>
        </div>
      </div>
      {/* Fereastra comuna a zonei, ca la Create Company. */}
      <Modal
        id="addNewCategory"
        open={open}
        title="New category"
        onClose={() => setOpen(false)}
        footer={
          <>
            <button type="button" className="btn btn-default" onClick={() => setOpen(false)}>
              Close
            </button>
            <button type="button" className="btn btn-primary saveCategory" onClick={() => void onSave()}>
              Save
            </button>
          </>
        }
      >
        <form
          className="form-horizontal labelcustomize"
          id="newCategoryForm"
          data-collabel="3"
          data-alignlabel="left"
          data-label="color"
          method="post"
          action="/settings/opteemum/newCategory"
        >
          <div className="form-group">
            <label className="col-sm-3 control-label" style={{ textAlign: 'left' }}>
              <span className="color">Category name</span>
            </label>
            <div className="col-sm-9">
              <input className="form-control" name="category_name" type="text" value={name} onChange={(ev) => setName(ev.target.value)} />
            </div>
          </div>
        </form>
      </Modal>
    </>
  )
}

/**
 * Cele patru etichete, cu pastila fiecareia. `label-default` e griul pentru
 * "fara eticheta"; celelalte trei sunt vestirile zonei din `od_console.css` -
 * fundal deschis, scris inchis. `label-primary` NU are regula acolo, deci ar fi
 * ramas albastrul lui Bootstrap cu scris alb, singura pastila care nu seamana
 * cu celelalte.
 */
const TAGS = [
  { id: 0, label: 'Untagged', pill: 'label-default' },
  { id: 1, label: 'Licensed', pill: 'label-info' },
  { id: 2, label: 'Unproductive', pill: 'label-warning' },
  { id: 3, label: 'Suspected', pill: 'label-danger' },
] as const

/**
 * Software > Software Tagging.
 *
 * Geamanul lui Settings > Softsee > Software Tagging. Inainte: `h4` cu
 * subtitlu, si atat - un tabel fara cap de coloana, fara cautare, fara
 * paginare, cu `<select>`-uri brute de 656px si fara nicio coloana de alegere,
 * desi ecranul ESTE despre cele patru etichete. Eticheta de acum nu se vedea
 * nicaieri: sertarul o arata, dar numai daca te uitai in el.
 */
function TaggingPanel() {
  const query = useQueryClient()
  const [tag, setTag] = useState(0)
  const [start, setStart] = useState(0)
  const [length, setLength] = useState(25)
  const [search, setSearch] = useState('')
  const table = useQuery({
    ...taggingTableQuery({ tag, start, length, search }),
    placeholderData: keepPreviousData,
  })
  if (table.error instanceof ApiError && table.error.status === 403) return <Forbidden />
  if (table.isPending) return <Spinner />
  const onChange = async (application_id: number, next: string) => {
    try {
      await changeApplicationTag(application_id, next)
      await query.invalidateQueries({ queryKey: ['settings', 'opteemum', 'tagging-table'] })
    } catch (err) {
      newAlert(err instanceof Error ? err.message : 'Error', 'danger')
    }
  }
  const tagOptions = TAGS.map((t) => ({ value: String(t.id), label: t.label }))
  /* O eticheta noua din coloana reia lista de la prima pagina. */
  const pick = (next: number) => {
    setTag(next)
    setStart(0)
  }
  const rows = table.data?.data ?? []
  return (
    <div id="content">
      {/* Ecranul n-are ce crea, deci n-are unealta in cap. */}
      <SettingsHead strong="Software" rest="tagging" subtitle="software tagging system" />
      <div className="row">
        <div className="col-md-3">
          <SettingsPicker id="tags-list">
            {TAGS.map((t) => (
              <SettingsPickerItem
                key={t.id}
                label={t.label}
                active={tag === t.id}
                onPick={() => pick(t.id)}
                liProps={{ 'data-tag': t.id }}
              />
            ))}
          </SettingsPicker>
        </div>
        <div className="col-md-9">
          <SoftseeListChrome
            id="taggingSettingsTable"
            search={search}
            onSearch={(next) => {
              setSearch(next)
              setStart(0)
            }}
            start={start}
            length={length}
            total={table.data?.recordsFiltered ?? 0}
            fetching={table.isFetching}
            onStart={setStart}
            onLength={(next) => {
              setLength(next)
              setStart(0)
            }}
          >
            <table id="taggingSettingsTable" className="table table-striped table-hover table-bordered dataTableM dataTable no-footer softwares-tagging-table">
              {/* Coloanele au latimea lor, deci eticheta si sertarul stau la acelasi x pe toate randurile. */}
              <colgroup>
                <col />
                <col style={{ width: 180 }} />
                <col style={{ width: 260 }} />
              </colgroup>
              <thead>
                <tr>
                  <th className="td-small a-left">Application</th>
                  <th className="td-small a-left">Tag</th>
                  <th className="td-small a-left">Change tag</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr className="odd">
                    <td colSpan={3} className="dataTables_empty">
                      No data available in table
                    </td>
                  </tr>
                ) : (
                  rows.map((app) => {
                    const current = TAGS.find((t) => t.id === num(app, 'tag_type')) ?? TAGS[0]
                    return (
                      <tr key={num(app, 'ID')} id={'row_' + num(app, 'ID')}>
                        <td className="td-small a-left">{str(app, 'short_name') || <i className="fas fa-minus font-10"></i>}</td>
                        <td className="td-small a-left">
                          <span className={'label ' + current.pill}>{current.label}</span>
                        </td>
                        <td className="td-small a-left">
                          <form className="changeTagForm" action="/settings/opteemum/changeApplicationTag" method="post">
                            <input type="hidden" value={num(app, 'ID')} name="application_id" />
                            {/* `SelectPicker` CU `form-control` pe invelis. */}
                            <SelectPicker
                              id={'applicationTag_' + num(app, 'ID')}
                              className="form-control input-sm applicationTag"
                              name="tag"
                              title="Select tag..."
                              value={String(num(app, 'tag_type'))}
                              onChange={(value) => void onChange(num(app, 'ID'), value)}
                              options={tagOptions}
                            />
                          </form>
                        </td>
                      </tr>
                    )
                  })
                )}
              </tbody>
            </table>
          </SoftseeListChrome>
        </div>
      </div>
    </div>
  )
}

/**
 * Data ultimei atingeri, ca in portal (`moment(...).format('D MMM YYYY H:mm')`).
 * Ce nu se citeste ca data ramane cum a venit: mai bine un sir ciudat decat
 * "Invalid Date" pe toata coloana.
 */
function when(value: string): string {
  if (value === '') return ''
  const at = new Date(value.replace(' ', 'T'))
  if (Number.isNaN(at.getTime())) return value

  return at.toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}

/**
 * Software > Software Kits.
 *
 * Geamanul lui Settings > Softsee > Manage Kits. Inainte: capul fara subtitlu,
 * butonul verde cazut sub linie in dreptul listei, doua coloane cu capul scris
 * cu litere mici (`name`, `version`), si o fereastra fara nicio eticheta - sapte
 * campuri unul sub altul, din care nu se intelegea care ce cere.
 *
 * SERTARUL "Pack" NU SE VEDEA DELOC. Optiunile se scriau din campul `pack`, dar
 * coloana din baza se cheama `pack_name` (`it_softsee_packs`, migrarea
 * Version20260821000106), deci fiecare optiune iesea cu eticheta goala: o lista
 * de randuri albe. Acum se citeste `pack_name`, cu `pack` ca plasa de siguranta.
 *
 * Fisierul MSI se alege dintr-un camp care SE VEDE. Sora de pe Softsee are
 * butonul "Upload MSI" al portalului cu `input[type=file]` ascuns
 * (`visibility: hidden`), care in portal era deschis de JavaScript-ul lui; in
 * v4 nu-l deschide nimeni, deci acolo fisierul nu se poate alege - si fara el
 * `addKit` raspunde "An error occured". Aici campul ramane la vedere.
 */
function KitsPanel() {
  const query = useQueryClient()
  const page = useQuery(kitsQuery())
  const [start, setStart] = useState(0)
  const [length, setLength] = useState(25)
  const [search, setSearch] = useState('')
  const table = useQuery({
    ...kitsTableQuery({ start, length, search }),
    placeholderData: keepPreviousData,
  })
  const [open, setOpen] = useState(false)
  /* Sertarele consolei sunt controlate, deci valorile lor stau aici. Categoria
     si pachetul se seamana cu PRIMUL rand, fiindca asta alegea si `<select>`-ul
     brut cand nimeni nu-i dadea o valoare. */
  const [category, setCategory] = useState('')
  const [pack, setPack] = useState('')
  const [os, setOs] = useState<string[]>([])
  useEffect(() => {
    if (!page.data) return
    const firstCategory = page.data.app_categories[0]
    const firstPack = page.data.packs[0]
    setCategory(firstCategory ? String(num(firstCategory, 'ID')) : '')
    setPack(firstPack ? String(num(firstPack, 'ID')) : '')
  }, [page.data])
  if (page.error instanceof ApiError && page.error.status === 403) return <Forbidden />
  if (!page.data) return <Spinner />
  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    try {
      await addKit(new FormData(event.currentTarget))
      toast('Kit saved.', '', 'success')
      setOpen(false)
      await query.invalidateQueries({ queryKey: ['settings', 'opteemum', 'kits-table'] })
    } catch (err) {
      newAlert(err instanceof Error ? err.message : 'Error', 'danger')
    }
  }
  const onRemove = async (kit: number) => {
    try {
      await removeKit(kit)
      await query.invalidateQueries({ queryKey: ['settings', 'opteemum', 'kits-table'] })
    } catch (err) {
      newAlert(err instanceof Error ? err.message : 'Error', 'danger')
    }
  }
  const packLabel = (row: Record<string, unknown>) => str(row, 'pack_name') || str(row, 'pack')
  const rows = table.data?.data ?? []
  return (
    <>
      <div id="content">
        <SettingsHead
          strong="Manage"
          rest="kits"
          subtitle="list of current software kits"
          tools={
            <button type="button" className="pull-right btn btn-success btn-sm" onClick={() => setOpen(true)}>
              <i className="fas fa-plus-circle" aria-hidden="true"></i> Add kit
            </button>
          }
        />
        <SoftseeListChrome
          id="kitsSettingsTable"
          search={search}
          onSearch={(next) => {
            setSearch(next)
            setStart(0)
          }}
          start={start}
          length={length}
          total={table.data?.recordsFiltered ?? 0}
          fetching={table.isFetching}
          onStart={setStart}
          onLength={(next) => {
            setLength(next)
            setStart(0)
          }}
        >
          <table id="kitsSettingsTable" className="table table-striped table-hover table-bordered dataTableM dataTable no-footer">
            <colgroup>
              <col />
              <col style={{ width: 110 }} />
              <col style={{ width: 200 }} />
              <col style={{ width: 160 }} />
              <col style={{ width: 170 }} />
              <col style={{ width: 110 }} />
            </colgroup>
            <thead>
              <tr>
                <th className="td-small a-left">Application</th>
                <th className="td-small a-left">Version</th>
                <th className="td-small a-left">Msi</th>
                <th className="td-small a-left">Exe</th>
                <th className="td-small a-left">Updated</th>
                <th className="td-small a-left"></th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr className="odd">
                  <td colSpan={6} className="dataTables_empty">
                    No data available in table
                  </td>
                </tr>
              ) : (
                rows.map((kit) => (
                  <tr key={num(kit, 'ID')} id={'row_' + num(kit, 'ID')}>
                    <td className="td-small a-left">{str(kit, 'name') || <i className="fas fa-minus font-10"></i>}</td>
                    <td className="td-small a-left">
                      {/*
                       * Versiunea e o eticheta, nu o stare: griul zonei. In portal
                       * era `label-primary`, care NU are regula in `od_console.css`,
                       * deci ar fi ramas albastrul lui Bootstrap cu scris alb - o
                       * culoare cu inteles, pusa pe ceva care n-are niciunul.
                       */}
                      {str(kit, 'version') ? <span className="label label-default">{str(kit, 'version')}</span> : <i className="fas fa-minus font-10"></i>}
                    </td>
                    <td className="td-small a-left">{str(kit, 'msi_name')}</td>
                    <td className="td-small a-left">{str(kit, 'exe')}</td>
                    <td className="td-small a-left">{when(str(kit, 'updated') || str(kit, 'created'))}</td>
                    <td className="td-small a-left">
                      <form className="removeKit" method="post" action="/settings/opteemum/removeKit">
                        <input type="hidden" name="software" value={num(kit, 'ID')} />
                        <button className="btn btn-sm btn-danger" type="button" onClick={() => void onRemove(num(kit, 'ID'))}>
                          <i className="fas fa-trash"></i> Remove
                        </button>
                      </form>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </SoftseeListChrome>
      </div>
      {/* Fereastra comuna a zonei, cu etichete la stanga ca in machetele portalului. */}
      <Modal
        id="myKitModal"
        open={open}
        title="Add new kit"
        onClose={() => setOpen(false)}
        footer={
          <>
            <button type="button" className="btn btn-default" onClick={() => setOpen(false)}>
              Close
            </button>
            <button type="submit" form="newKitForm" className="btn btn-primary goSaveKit">
              <i className="far fa-save"></i> Save
            </button>
          </>
        }
      >
        <form
          className="form-horizontal labelcustomize"
          id="newKitForm"
          data-collabel="3"
          data-alignlabel="left"
          data-label="color"
          method="post"
          action="/settings/opteemum/addKit"
          onSubmit={(ev) => void onSubmit(ev)}
        >
          <div className="form-group">
            <label className="col-sm-3 control-label" style={{ textAlign: 'left' }}>
              <span className="color">MSI</span>
            </label>
            <div className="col-sm-9">
              <input id="upload_msi" className="form-control" name="msi" type="file" required />
              <span className="help-block">
                <i className="fas fa-info-circle"></i> Fara fisier, kitul nu se poate scrie
              </span>
            </div>
          </div>
          <div className="form-group">
            <label className="col-sm-3 control-label" style={{ textAlign: 'left' }}>
              <span className="color">Software name</span>
            </label>
            <div className="col-sm-9">
              <input className="form-control" name="kit_name" type="text" required />
            </div>
          </div>
          <div className="form-group">
            <label className="col-sm-3 control-label" style={{ textAlign: 'left' }}>
              <span className="color">Software version</span>
            </label>
            <div className="col-sm-9">
              <input className="form-control" name="kit_version" type="text" required />
            </div>
          </div>
          <div className="form-group">
            <label className="col-sm-3 control-label" style={{ textAlign: 'left' }}>
              <span className="color">Category</span>
            </label>
            <div className="col-sm-9">
              <SelectPicker
                id="newKitCategory"
                className="form-control"
                name="kit_category"
                title="Select.."
                value={category}
                onChange={setCategory}
                options={page.data.app_categories.map((cat) => ({ value: String(num(cat, 'ID')), label: str(cat, 'category_name') }))}
              />
            </div>
          </div>
          <div className="form-group">
            <label className="col-sm-3 control-label" style={{ textAlign: 'left' }}>
              <span className="color">Pack</span>
            </label>
            <div className="col-sm-9">
              {/* `pack_name`, nu `pack`: vezi capul panoului. */}
              <SelectPicker
                id="newKitPack"
                className="form-control"
                name="kit_pack"
                title="Select.."
                value={pack}
                onChange={setPack}
                options={page.data.packs.map((row) => ({ value: String(num(row, 'ID')), label: packLabel(row) }))}
              />
            </div>
          </div>
          <div className="form-group">
            <label className="col-sm-3 control-label" style={{ textAlign: 'left' }}>
              <span className="color">Exe</span>
            </label>
            <div className="col-sm-9">
              <input className="form-control" name="kit_exe" type="text" />
              <span className="help-block">
                <i className="fas fa-info-circle"></i> Executable to close when the script is ran
              </span>
            </div>
          </div>
          <div className="form-group">
            <label className="col-sm-3 control-label" style={{ textAlign: 'left' }}>
              <span className="color">Params</span>
            </label>
            <div className="col-sm-9">
              <textarea style={{ maxWidth: '100%' }} className="form-control" name="kit_params"></textarea>
              <span className="help-block">
                <i className="fas fa-info-circle"></i> /qn Reboot=ReallySuppress AllUsers=1
              </span>
            </div>
          </div>
          <div className="form-group">
            <label className="col-sm-3 control-label" style={{ textAlign: 'left' }}>
              <span className="color">Operating system</span>
            </label>
            <div className="col-sm-9">
              <SelectPicker
                id="newKitOs"
                className="form-control"
                name="kit_os[]"
                title="Select.."
                multiple
                selectedTextFormat="count > 2"
                size={10}
                value={os}
                onChange={setOs}
                options={page.data.operating_systems.map((row) => ({ value: String(num(row, 'ID')), label: str(row, 'Name') }))}
              />
            </div>
          </div>
        </form>
      </Modal>
    </>
  )
}

/**
 * Software > Software Custom Kits.
 *
 * Geamanul lui Settings > Softsee > Manage Custom Kits. Inainte: capul fara
 * subtitlu, butonul verde cazut sub linie, doua coloane, si o fereastra de
 * cinci campuri fara nicio eticheta - din care lipseau EXE, parametrii,
 * sistemul de operare si pachetele, desi `addCustomKit` le scrie pe toate de
 * la inceput (`exe_to_kill`, `parameters`, `os[]`, `packages[]`).
 *
 * Firma se seamana cu firma OMULUI, nu cu prima din lista: `companies` sunt
 * toate firmele, iar `addCustomKit` primeste numai firma lui - orice alta
 * raspunde "not found", deci fereastra se deschidea pe o alegere care cadea la
 * salvare.
 */
function CustomKitsPanel() {
  const query = useQueryClient()
  const page = useQuery(customKitsQuery())
  const [start, setStart] = useState(0)
  const [length, setLength] = useState(25)
  const [search, setSearch] = useState('')
  const table = useQuery({
    ...customKitsTableQuery({ start, length, search }),
    placeholderData: keepPreviousData,
  })
  const [open, setOpen] = useState(false)
  const [company, setCompany] = useState('')
  const [category, setCategory] = useState('')
  const [os, setOs] = useState<string[]>([])
  const [packages, setPackages] = useState<string[]>([])
  useEffect(() => {
    if (!page.data) return
    setCompany(page.data.company_id > 0 ? String(page.data.company_id) : '')
    const first = page.data.categories[0]
    setCategory(first ? String(num(first, 'ID')) : '')
  }, [page.data])
  if (page.error instanceof ApiError && page.error.status === 403) return <Forbidden />
  if (!page.data) return <Spinner />
  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    try {
      await addCustomKit(new FormData(event.currentTarget))
      toast('Kit saved.', '', 'success')
      setOpen(false)
      await query.invalidateQueries({ queryKey: ['settings', 'opteemum', 'custom-kits-table'] })
    } catch (err) {
      newAlert(err instanceof Error ? err.message : 'Error', 'danger')
    }
  }
  const onRemove = async (kit: number) => {
    try {
      await deleteCustomKit(kit)
      await query.invalidateQueries({ queryKey: ['settings', 'opteemum', 'custom-kits-table'] })
    } catch (err) {
      newAlert(err instanceof Error ? err.message : 'Error', 'danger')
    }
  }
  const rows = table.data?.data ?? []
  return (
    <>
      <div id="content">
        <SettingsHead
          strong="Manage"
          rest="custom kits"
          subtitle="list of current custom software kits"
          tools={
            <button type="button" className="pull-right btn btn-success btn-sm" onClick={() => setOpen(true)}>
              <i className="fas fa-plus-circle" aria-hidden="true"></i> Add custom kit
            </button>
          }
        />
        <SoftseeListChrome
          id="customKitsTable"
          search={search}
          onSearch={(next) => {
            setSearch(next)
            setStart(0)
          }}
          start={start}
          length={length}
          total={table.data?.recordsFiltered ?? 0}
          fetching={table.isFetching}
          onStart={setStart}
          onLength={(next) => {
            setLength(next)
            setStart(0)
          }}
        >
          <table id="customKitsTable" className="table table-striped table-hover table-bordered dataTableM dataTable no-footer">
            <colgroup>
              <col />
              <col style={{ width: 110 }} />
              <col style={{ width: 200 }} />
              <col style={{ width: 200 }} />
              <col style={{ width: 110 }} />
            </colgroup>
            <thead>
              <tr>
                <th className="td-small a-left">Name</th>
                <th className="td-small a-left">Version</th>
                <th className="td-small a-left">Msi</th>
                <th className="td-small a-left">Category</th>
                <th className="td-small a-left"></th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr className="odd">
                  <td colSpan={5} className="dataTables_empty">
                    No data available in table
                  </td>
                </tr>
              ) : (
                rows.map((kit) => (
                  <tr key={num(kit, 'ID')} id={'rowd_' + num(kit, 'ID')}>
                    <td className="td-small a-left">{str(kit, 'name') || <i className="fas fa-minus font-10"></i>}</td>
                    <td className="td-small a-left">
                      {/* Versiunea e o eticheta, nu o stare: griul zonei. */}
                      {str(kit, 'version') ? <span className="label label-default">{str(kit, 'version')}</span> : <i className="fas fa-minus font-10"></i>}
                    </td>
                    <td className="td-small a-left">{str(kit, 'msi')}</td>
                    <td className="td-small a-left">
                      {str(kit, 'category_name') ? str(kit, 'category_name') : <span className="label label-default">No category</span>}
                    </td>
                    <td className="td-small a-left">
                      <form className="deleteCustomKit" method="post" action="/settings/opteemum/deleteCustomKit">
                        <input type="hidden" name="ID" value={num(kit, 'ID')} />
                        <button className="btn btn-sm btn-danger" type="button" onClick={() => void onRemove(num(kit, 'ID'))}>
                          <i className="fas fa-trash"></i> Delete
                        </button>
                      </form>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </SoftseeListChrome>
      </div>
      <Modal
        id="addCustomKitForm"
        open={open}
        title="Add new custom kit"
        onClose={() => setOpen(false)}
        footer={
          <>
            <button type="button" className="btn btn-default" onClick={() => setOpen(false)}>
              Close
            </button>
            <button type="submit" form="newCustomKitForm" className="btn btn-primary saveCustomKit">
              <i className="far fa-save"></i> Save
            </button>
          </>
        }
      >
        <form
          className="form-horizontal labelcustomize"
          id="newCustomKitForm"
          data-collabel="3"
          data-alignlabel="left"
          data-label="color"
          method="post"
          action="/settings/opteemum/addCustomKit"
          onSubmit={(ev) => void onSubmit(ev)}
        >
          <div className="form-group">
            <label className="col-sm-3 control-label" style={{ textAlign: 'left' }}>
              <span className="color">MSI</span>
            </label>
            <div className="col-sm-9">
              <input id="upload_custom_msi" className="form-control" name="msi" type="file" required />
              <span className="help-block">
                <i className="fas fa-info-circle"></i> Fara fisier, kitul nu se poate scrie
              </span>
            </div>
          </div>
          <div className="form-group">
            <label className="col-sm-3 control-label" style={{ textAlign: 'left' }}>
              <span className="color">Software name</span>
            </label>
            <div className="col-sm-9">
              <input className="form-control" name="name" type="text" required />
            </div>
          </div>
          <div className="form-group">
            <label className="col-sm-3 control-label" style={{ textAlign: 'left' }}>
              <span className="color">Software version</span>
            </label>
            <div className="col-sm-9">
              <input className="form-control" name="version" type="text" required />
            </div>
          </div>
          <div className="form-group">
            <label className="col-sm-3 control-label" style={{ textAlign: 'left' }}>
              <span className="color">Company</span>
            </label>
            <div className="col-sm-9">
              <SelectPicker
                id="newCustomKitCompany"
                className="form-control"
                name="company"
                title="Select.."
                value={company}
                onChange={setCompany}
                options={page.data.companies.map((c) => ({ value: String(num(c, 'ID')), label: str(c, 'Nume') }))}
              />
            </div>
          </div>
          <div className="form-group">
            <label className="col-sm-3 control-label" style={{ textAlign: 'left' }}>
              <span className="color">Category</span>
            </label>
            <div className="col-sm-9">
              <SelectPicker
                id="newCustomKitCategory"
                className="form-control"
                name="category"
                title="Select.."
                value={category}
                onChange={setCategory}
                options={page.data.categories.map((cat) => ({ value: String(num(cat, 'ID')), label: str(cat, 'category_name') }))}
              />
            </div>
          </div>
          <div className="form-group">
            <label className="col-sm-3 control-label" style={{ textAlign: 'left' }}>
              <span className="color">Exe</span>
            </label>
            <div className="col-sm-9">
              <input className="form-control" name="exe_to_kill" type="text" />
              <span className="help-block">
                <i className="fas fa-info-circle"></i> Executable to close when the script is ran
              </span>
            </div>
          </div>
          <div className="form-group">
            <label className="col-sm-3 control-label" style={{ textAlign: 'left' }}>
              <span className="color">Params</span>
            </label>
            <div className="col-sm-9">
              <textarea style={{ maxWidth: '100%' }} className="form-control" name="parameters"></textarea>
            </div>
          </div>
          <div className="form-group">
            <label className="col-sm-3 control-label" style={{ textAlign: 'left' }}>
              <span className="color">Operating system</span>
            </label>
            <div className="col-sm-9">
              <SelectPicker
                id="newCustomKitOs"
                className="form-control"
                name="os[]"
                title="Select.."
                multiple
                selectedTextFormat="count > 2"
                size={10}
                value={os}
                onChange={setOs}
                options={page.data.operating_systems.map((row) => ({ value: String(num(row, 'ID')), label: str(row, 'Name') }))}
              />
            </div>
          </div>
          <div className="form-group">
            <label className="col-sm-3 control-label" style={{ textAlign: 'left' }}>
              <span className="color">Add to Package</span>
            </label>
            <div className="col-sm-9">
              <SelectPicker
                id="newCustomKitPackages"
                className="form-control"
                name="packages[]"
                title="Select.."
                multiple
                selectedTextFormat="count > 2"
                size={10}
                value={packages}
                onChange={setPackages}
                options={page.data.packages.map((pkg) => ({ value: String(num(pkg, 'ID')), label: str(pkg, 'Name') }))}
              />
            </div>
          </div>
        </form>
      </Modal>
    </>
  )
}

/**
 * Software > Custom Packages.
 *
 * Geamanul lui Settings > Softsee > Manage Custom Packages. Inainte: capul
 * fara subtitlu, butonul verde cazut sub linie, si o fereastra de trei campuri
 * fara etichete.
 *
 * CELE DOUA SERTARE DE KITURI lipseau cu totul, desi capatul le trimitea
 * (`custom_kits`, `main_kits`) si `addCustomPackage` le SCRIE - le trece prin
 * `addKitToPackage`. Un pachet facut din v4 iesea deci intotdeauna gol, si
 * trebuia umplut pe urma din portalul vechi. Acum sunt in fereastra, si pleaca
 * la server.
 *
 * (Pe sora de pe Softsee cele doua sertare sunt desenate dar NU se trimit -
 * acolo `addCustomPackage` trimite doar numele, descrierea si firma. Acolo e
 * o hiba inca neatinsa; aici capatul stia de la inceput sa le scrie.)
 */
function PackagesPanel() {
  const query = useQueryClient()
  const page = useQuery(packagesQuery())
  const [start, setStart] = useState(0)
  const [length, setLength] = useState(25)
  const [search, setSearch] = useState('')
  const table = useQuery({
    ...packagesTableQuery({ start, length, search }),
    placeholderData: keepPreviousData,
  })
  const [open, setOpen] = useState(false)
  const [company, setCompany] = useState('')
  const [customKits, setCustomKits] = useState<string[]>([])
  const [standardKits, setStandardKits] = useState<string[]>([])
  useEffect(() => {
    if (!page.data) return
    setCompany(page.data.company_id > 0 ? String(page.data.company_id) : '')
  }, [page.data])
  if (page.error instanceof ApiError && page.error.status === 403) return <Forbidden />
  if (!page.data) return <Spinner />
  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const form = new FormData(event.currentTarget)
    try {
      await addCustomPackage({
        name: String(form.get('name') ?? ''),
        description: String(form.get('description') ?? ''),
        company,
        /* Kiturile alese pleaca si ele: `addCustomPackage` le trece prin
           `addKitToPackage`, cu tipul 1 pentru cele proprii si 2 pentru cele
           standard. */
        custom_kits: customKits,
        standard_kits: standardKits,
      })
      toast('Package saved.', '', 'success')
      setOpen(false)
      setCustomKits([])
      setStandardKits([])
      await query.invalidateQueries({ queryKey: ['settings', 'opteemum', 'packages-table'] })
    } catch (err) {
      newAlert(err instanceof Error ? err.message : 'Error', 'danger')
    }
  }
  const onRemove = async (pkg: number) => {
    try {
      await deleteCustomPackage(pkg)
      await query.invalidateQueries({ queryKey: ['settings', 'opteemum', 'packages-table'] })
    } catch (err) {
      newAlert(err instanceof Error ? err.message : 'Error', 'danger')
    }
  }
  const rows = table.data?.data ?? []
  return (
    <>
      <div id="content">
        <SettingsHead
          strong="Manage"
          rest="custom packages"
          subtitle="list of current custom packages"
          tools={
            <button type="button" className="pull-right btn btn-success btn-sm" onClick={() => setOpen(true)}>
              <i className="fas fa-plus-circle" aria-hidden="true"></i> Add custom package
            </button>
          }
        />
        <SoftseeListChrome
          id="packagesTable"
          search={search}
          onSearch={(next) => {
            setSearch(next)
            setStart(0)
          }}
          start={start}
          length={length}
          total={table.data?.recordsFiltered ?? 0}
          fetching={table.isFetching}
          onStart={setStart}
          onLength={(next) => {
            setLength(next)
            setStart(0)
          }}
        >
          <table id="packagesTable" className="table table-striped table-hover table-bordered dataTableM dataTable no-footer">
            <colgroup>
              <col style={{ width: 320 }} />
              <col />
              <col style={{ width: 110 }} />
            </colgroup>
            <thead>
              <tr>
                <th className="td-small a-left">Name</th>
                <th className="td-small a-left">Description</th>
                <th className="td-small a-left"></th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr className="odd">
                  <td colSpan={3} className="dataTables_empty">
                    No data available in table
                  </td>
                </tr>
              ) : (
                rows.map((pkg) => (
                  <tr key={num(pkg, 'ID')} id={'row_' + num(pkg, 'ID')}>
                    <td className="td-small a-left">{str(pkg, 'Name') || <i className="fas fa-minus font-10"></i>}</td>
                    <td className="td-small a-left">{str(pkg, 'Description') || <i className="fas fa-minus font-10"></i>}</td>
                    <td className="td-small a-left">
                      <form className="deleteCustomPackage" method="post" action="/settings/opteemum/deleteCustomPackage">
                        <input type="hidden" name="ID" value={num(pkg, 'ID')} />
                        <button className="btn btn-sm btn-danger" type="button" onClick={() => void onRemove(num(pkg, 'ID'))}>
                          <i className="fas fa-trash"></i> Delete
                        </button>
                      </form>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </SoftseeListChrome>
      </div>
      <Modal
        id="addCustomPackageForm"
        open={open}
        title="Add new custom package"
        onClose={() => setOpen(false)}
        footer={
          <>
            <button type="button" className="btn btn-default" onClick={() => setOpen(false)}>
              Close
            </button>
            <button type="submit" form="newCustomPackageForm" className="btn btn-primary saveCustomPackage">
              <i className="far fa-save"></i> Save
            </button>
          </>
        }
      >
        <form
          className="form-horizontal labelcustomize"
          id="newCustomPackageForm"
          data-collabel="3"
          data-alignlabel="left"
          data-label="color"
          method="post"
          action="/settings/opteemum/addCustomPackage"
          onSubmit={(ev) => void onSubmit(ev)}
        >
          <div className="form-group">
            <label className="col-sm-3 control-label" style={{ textAlign: 'left' }}>
              <span className="color">Package name</span>
            </label>
            <div className="col-sm-9">
              <input className="form-control" name="name" type="text" required />
            </div>
          </div>
          <div className="form-group">
            <label className="col-sm-3 control-label" style={{ textAlign: 'left' }}>
              <span className="color">Description</span>
            </label>
            <div className="col-sm-9">
              <input className="form-control" name="description" type="text" />
            </div>
          </div>
          <div className="form-group">
            <label className="col-sm-3 control-label" style={{ textAlign: 'left' }}>
              <span className="color">Company</span>
            </label>
            <div className="col-sm-9">
              <SelectPicker
                id="newCustomPackageCompany"
                className="form-control"
                name="company"
                title="Select.."
                value={company}
                onChange={setCompany}
                options={page.data.companies.map((c) => ({ value: String(num(c, 'ID')), label: str(c, 'Nume') }))}
              />
            </div>
          </div>
          <div className="form-group">
            <label className="col-sm-3 control-label" style={{ textAlign: 'left' }}>
              <span className="color">Add Custom Kits</span>
            </label>
            <div className="col-sm-9">
              {/* Fara `required`: `SelectPicker` tine `<select>`-ul adevarat ascuns,
                  iar un camp ascuns si `required` opreste trimiterea cu "not
                  focusable", fara sa spuna omului nimic. */}
              <SelectPicker
                id="newCustomPackageCustomKits"
                className="form-control"
                name="custom_kits[]"
                title="Select.."
                multiple
                selectedTextFormat="count > 2"
                size={10}
                value={customKits}
                onChange={setCustomKits}
                options={page.data.custom_kits.map((kit) => ({ value: String(num(kit, 'ID')), label: str(kit, 'Name') }))}
              />
            </div>
          </div>
          <div className="form-group">
            <label className="col-sm-3 control-label" style={{ textAlign: 'left' }}>
              <span className="color">Add Standard Kits</span>
            </label>
            <div className="col-sm-9">
              <SelectPicker
                id="newCustomPackageStandardKits"
                className="form-control"
                name="standard_kits[]"
                title="Select.."
                multiple
                selectedTextFormat="count > 2"
                size={10}
                value={standardKits}
                onChange={setStandardKits}
                options={page.data.main_kits.map((kit) => ({ value: String(num(kit, 'ID')), label: str(kit, 'name') }))}
              />
            </div>
          </div>
        </form>
      </Modal>
    </>
  )
}

function StartupPanel() {
  const query = useQueryClient()
  const table = useQuery(startupTableQuery())
  if (table.error instanceof ApiError && table.error.status === 403) return <Forbidden />
  if (!table.data) return <Spinner />
  const act = async (id: number, kind: 'approve' | 'disapprove') => {
    try {
      if (kind === 'approve') await approveApplication(id)
      else await disapproveApplication(id)
      await query.invalidateQueries({ queryKey: ['settings', 'opteemum', 'startup-table'] })
    } catch (err) {
      newAlert(err instanceof Error ? err.message : 'Error', 'danger')
    }
  }
  return (
    <div id="content">
      {/*
       * Capul zonei. `Heading` scria doar titlul si linia, deci ecranul
       * n-avea randul gri de sub el (masurat: `label.color` lipsea din
       * pagina). Iar "Startup apps" nu spune CE lista e: in portal ecranul se
       * cheama "Startup applications", cu "list of removed startup
       * applications" sub el - alea sunt, aplicatiile scoase din pornire.
       */}
      <SettingsHead
        strong="Startup"
        rest="applications"
        subtitle={
          <>
            List of <strong>removed startup applications</strong>
          </>
        }
      />
      {/*
       * `dataTableM` si `table-bordered` au cazut: primul e chenarul pe care
       * DataTables il pune pe tabelele LUI, si aici nu e niciun DataTable -
       * randurile vin din `startupTableQuery`. Al doilea desena o cutie in
       * jurul fiecarei celule, ceea ce nu face niciun alt tabel din zona.
       *
       * Si coloana butoanelor are acum latimea ei (220px), la dreapta: inainte
       * era o coloana de 585px cu doua butoane mici lipite la stanga ei.
       */}
      <table className="table table-striped table-hover" id="startupAppsTable">
        <thead>
          <tr>
            <th>Application</th>
            <th className="text-right" style={{ width: 220 }}>
              Change
            </th>
          </tr>
        </thead>
        <tbody>
          {(table.data.data ?? []).length === 0 ? (
            /* O lista goala se spune, nu se lasa un tabel fara randuri. */
            <tr>
              <td colSpan={2}>
                <span className="label label-default">Nothing removed yet.</span> Startup applications taken off a machine show up here.
              </td>
            </tr>
          ) : (
            (table.data.data ?? []).map((row) => (
              <tr key={num(row, 'ID')}>
                {/*
                 * Coloana citea `Name`, si in coada `short_name` - doua campuri
                 * pe care randul nu le are, deci celula iesea goala pe fiecare
                 * rand. Randurile vin dintr-un `SELECT *` peste
                 * `it_removed_startup_applications`, iar acolo numele sta in
                 * `application_name` (asa il citeste si portalul vechi, in
                 * `Removed_startup_applications::getTableData`).
                 */}
                <td>{str(row, 'application_name')}</td>
                <td className="text-right text-nowrap">
                  <button className="btn btn-xs btn-success" onClick={() => void act(num(row, 'ID'), 'approve')}>
                    Approve
                  </button>{' '}
                  <button className="btn btn-xs btn-danger" onClick={() => void act(num(row, 'ID'), 'disapprove')}>
                    Disapprove
                  </button>
                  <input type="hidden" name="_application" value={num(row, 'ID')} />
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  )
}
